import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';
import { apiService } from '../services/api';
import { VoiceCloneCampaignItem } from '../components/crm/VoiceCloneCampaignItem';
import { fetchVoiceClones, type VoiceClone } from '../components/crm/VoiceCloneModals';
import { VoiceCampaignCostModal, type VoiceCampaignEstimate } from '../components/crm/VoiceCampaignCostModal';
import { CampaignMessagePreview } from '../components/campaigns/CampaignMessagePreview';
import { useCreditPricing } from '../hooks/useCreditPricing';
import { WahaHealthAlert } from '../components/whatsapp/WahaHealthAlert';
import type { WahaSessionHealth } from '../types/wahaHealth';

type MessageContent =
  | { text: string }
  | { url: string; caption?: string }
  | { url: string; fileName?: string }
  | { sequence: Array<{ type: string; content: any }> };

interface Campaign {
  id: string;
  nome: string;
  targetTags: string[];
  targetContactTags?: string[];
  sessionNames: string[];
  sessionName: string;
  messageType: string;
  messageContent: MessageContent;
  provider?: string | null;
  providerStatus?: string | null;
  externalReference?: string | null;
  providerSubmittedAt?: string | null;
  creditsCharged?: number | string | null;
  creditsRefundedAt?: string | null;
  channel?: string | null;
  targetFilterMode?: 'union' | 'intersection';
  targetTagMatch?: 'any' | 'all';
  lateAccountId?: string | null;
  lateTemplateName?: string | null;
  lateTemplateLang?: string | null;
  randomDelay: number;
  randomDelayMax: number | null;
  startImmediately: boolean;
  scheduledFor: string | null;
  scheduleStartDay: number | null;
  scheduleEndDay: number | null;
  scheduleStartHour: number | null;
  scheduleEndHour: number | null;
  status: string;
  totalContacts: number;
  sentCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  criadoEm: string;
  // Ações pós-envio (etiquetas, funil, atribuição da conversa). O backend
  // devolve o JSON cru; usado ao clonar a campanha.
  postSendActions?: Partial<PostSendActions> | null;
  // Mensagem própria por usuário do rodízio, chaveada pelo id do usuário.
  perUserMessages?: Record<string, any> | null;
  session: {
    name: string;
    displayName?: string;
    status: string;
    mePushName: string | null;
  };
  _count: {
    messages: number;
  };
}

interface WhatsAppSession {
  name: string; // Nome real usado na API
  displayName?: string; // Nome exibido ao usuário
  mePushName: string | null;
  meId: string | null;
  health?: WahaSessionHealth | null;
}

function isAllcanceCampaign(campaign: Campaign): boolean {
  return campaign.provider === 'allcance' || campaign.messageType === 'allcance';
}

function isLockedAllcanceCampaign(campaign: Campaign): boolean {
  return isAllcanceCampaign(campaign) && Boolean(
    campaign.providerSubmittedAt
    || campaign.externalReference
    || Number(campaign.creditsCharged || 0) > 0
    || campaign.creditsRefundedAt
  );
}

function providerSafeText(value: unknown, fallback: string): string {
  const text = String(value || '').trim();
  return (text || fallback).replace(/allcance/gi, 'provedor');
}

type WizardStep = 1 | 2 | 3 | 4;

// Ações aplicadas após o envio (etapa "Etiquetas e funil"): no contato
// (etiquetas e etapa do funil) e na conversa do CRM Chat (atribuição).
interface PostSendActions {
  removeAllTags: boolean;
  removeTags: string[];
  addTags: string[];
  moveToCategoriaId: string | null;
  // Usuários que recebem a conversa. Com mais de um, o disparo faz rodízio na
  // ORDEM desta lista (1º envio → 1º usuário, 2º → 2º, e volta ao começo).
  assignToUserIds: string[];
}

// Usuário do tenant, para atribuir a conversa gerada pelo disparo.
interface TenantUserOption {
  id: string;
  nome?: string;
  email: string;
  ativo?: boolean;
}

interface ContactTag {
  id: string;
  nome: string;
  funnelId?: string | null;
  funnelNome?: string | null;
  funnelCor?: string | null;
}

interface LateAccount {
  id: string;
  platformAccountId: string;
  accountName: string;
  accountUsername?: string;
  groupName?: string;
}

interface LateTemplate {
  id: string;
  name: string;
  language: string;
  status: string;
  components?: Array<{ type: string; text?: string }>;
  variableOrder?: string[];
}

// `link` é URL de destino, não campo do contato: vem de texto fixo no mapeamento
// (campanha) ou do payload (webhook). Ver CONTACT_TEMPLATE_VARIABLES no backend.
const CONTACT_TEMPLATE_VARIABLE_KEYS = new Set(['nome', 'primeironome', 'telefone', 'email', 'observacoes', 'categoria', 'link']);
const ORDER_TEMPLATE_VARIABLE_KEYS = new Set(['itens', 'numeropedido', 'numeronotafiscal', 'ecommerce']);

function extractVariableOrder(text: string): string[] {
  const seen = new Set<string>();
  const order: string[] = [];

  const re = /\{\{\s*([^}]+?)\s*\}\}/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const key = m[1].trim().toLowerCase();
    if (/^\d+$/.test(key)) continue;
    if (!seen.has(key)) { seen.add(key); order.push(key); }
  }
  return order;
}

const CAMPAIGN_VARIABLE_LABELS: Record<string, string> = {
  nome: 'Nome completo',
  primeironome: 'Primeiro nome',
  telefone: 'Telefone',
  email: 'E-mail',
  observacoes: 'Observações',
  categoria: 'Categoria',
  itens: 'Itens',
  numeropedido: 'Número do pedido',
  numeronotafiscal: 'Número da nota fiscal',
  ecommerce: 'E-commerce',
  link: 'Link (URL)',
};

function getTemplateBodyText(template: LateTemplate): string {
  const bodyComponent = template.components?.find(c => c.type === 'BODY');
  return bodyComponent?.text || '';
}

// Prefixo de posição mapeada para TEXTO FIXO (bate com LITERAL_PREFIX do backend).
const TEXT_MAPPING_PREFIX = 'text:';

// Rótulo legível de uma entrada de mapeamento, para exibição.
function mappingEntryLabel(entry: string): string {
  if (typeof entry === 'string' && entry.startsWith(TEXT_MAPPING_PREFIX)) {
    return `"${entry.slice(TEXT_MAPPING_PREFIX.length)}" (texto fixo)`;
  }
  return CAMPAIGN_VARIABLE_LABELS[entry] || entry;
}

// Mapeamento COMPLETO do template, na ordem de envio — inclui as posições de
// texto fixo e a do link de botão dinâmico. É isto que a campanha precisa
// guardar: qualquer posição omitida desloca todas as seguintes no envio.
function getTemplateOrder(template: LateTemplate): string[] {
  return template.variableOrder || extractVariableOrder(getTemplateBodyText(template)) || [];
}

// Só as VARIÁVEIS do mapeamento. Textos fixos são descartados: eles não dependem
// de dado de contato nem de pedido, então não podem tornar um template
// incompatível com campanhas (senão mapear uma posição como texto fixo bloquearia
// o template inteiro). Use apenas para validação de compatibilidade — nunca para
// montar o que será enviado.
function getTemplateVariables(template: LateTemplate): string[] {
  return getTemplateOrder(template).filter(entry => !String(entry).startsWith(TEXT_MAPPING_PREFIX));
}

function getTemplateIncompatibleVariables(template: LateTemplate): string[] {
  const variables = getTemplateVariables(template);
  return variables.filter(key => ORDER_TEMPLATE_VARIABLE_KEYS.has(key));
}

function getTemplateUnknownVariables(template: LateTemplate): string[] {
  const variables = getTemplateVariables(template);
  return variables.filter(key => !CONTACT_TEMPLATE_VARIABLE_KEYS.has(key) && !ORDER_TEMPLATE_VARIABLE_KEYS.has(key));
}

function isTemplateCompatibleWithCampaign(template: LateTemplate): boolean {
  return getTemplateIncompatibleVariables(template).length === 0 && getTemplateUnknownVariables(template).length === 0;
}

function isOfficialCampaign(campaign: Campaign | null): boolean {
  return campaign?.messageType === 'whatsapp_template';
}

export function CampaignsPage() {
  const { getMeta: getCreditPrice } = useCreditPricing();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  // Etapa 3: o que acontece com o contato depois que a mensagem sai.
  const [postSendActions, setPostSendActions] = useState<PostSendActions>({
    removeAllTags: false,
    removeTags: [],
    addTags: [],
    moveToCategoriaId: null,
    assignToUserIds: [],
  });
  const [newPostSendTag, setNewPostSendTag] = useState('');
  // Buscas próprias da etapa 3 — não podem compartilhar estado com as da etapa 1,
  // senão filtrar aqui mexeria na seleção de público.
  const [postSendRemoveSearch, setPostSendRemoveSearch] = useState('');
  const [postSendAddSearch, setPostSendAddSearch] = useState('');
  const [postSendCategorySearch, setPostSendCategorySearch] = useState('');
  const [postSendUserSearch, setPostSendUserSearch] = useState('');
  const [tenantUsers, setTenantUsers] = useState<TenantUserOption[]>([]);
  const [tenantUsersTruncated, setTenantUsersTruncated] = useState(false);
  const [deliveryProvider, setDeliveryProvider] = useState<'unofficial' | 'official' | 'allcance'>('unofficial');
  const [targetFilterMode, setTargetFilterMode] = useState<'union' | 'intersection'>('union');
  const [targetTagMatch, setTargetTagMatch] = useState<'any' | 'all'>('any');
  const [audiencePreview, setAudiencePreview] = useState<{
    totalContacts: number;
    totalRecipients?: number;
    duplicatePhones?: number;
    invalidPhones?: number;
    counts: { categories: Record<string, number>; tags: Record<string, number> };
    availableCounts?: { categories: Record<string, number>; tags: Record<string, number> };
    allcance?: {
      withButton: boolean;
      minimumContacts: number;
      featureKey: string;
      unitCost: number;
      estimatedCredits: number;
      eligible: boolean;
    };
  } | null>(null);
  const [syncingAllcanceId, setSyncingAllcanceId] = useState<string | null>(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [allcanceForm, setAllcanceForm] = useState({
    text: '',
    profileName: '',
    profilePhotoBase64: '',
    withButton: false,
    buttonText: '',
    buttonUrl: '',
  });
  const [cloneSourceName, setCloneSourceName] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [currentReportCampaignId, setCurrentReportCampaignId] = useState<string | null>(null);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportItemsPerPage] = useState(8);
  const [campaignsCurrentPage, setCampaignsCurrentPage] = useState(1);
  const [campaignsPerPage] = useState(10);
  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [contactEtiquetas, setContactEtiquetas] = useState<string[]>([]);
  const [contactEtiquetaColors, setContactEtiquetaColors] = useState<Record<string, string>>({});
  const [categorySearch, setCategorySearch] = useState('');
  const [etiquetaSearch, setEtiquetaSearch] = useState('');
  const [whatsappSessions, setWhatsappSessions] = useState<WhatsAppSession[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const [fileInfos, setFileInfos] = useState<{ [key: string]: { name: string, size: number, type: string } }>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  // Vozes clonadas do tenant, para o tipo de mensagem "Voz clonada".
  const [voiceClones, setVoiceClones] = useState<VoiceClone[]>([]);
  const [voiceEstimate, setVoiceEstimate] = useState<VoiceCampaignEstimate | null>(null);
  const [voiceEstimateOpen, setVoiceEstimateOpen] = useState(false);
  const [voiceEstimateLoading, setVoiceEstimateLoading] = useState(false);
  useEffect(() => {
    // Falha silenciosa: sem catálogo, o tipo "Voz clonada" só avisa que não há
    // voz pronta — não deve derrubar a tela de campanhas.
    fetchVoiceClones().then(setVoiceClones).catch(() => {});
  }, []);

  // WhatsApp Oficial (GetLate)
  const [useOfficialApi, setUseOfficialApi] = useState(false);
  const [lateAccounts, setLateAccounts] = useState<LateAccount[]>([]);
  const [formLateAccountId, setFormLateAccountId] = useState<string>('');
  const [lateTemplates, setLateTemplates] = useState<LateTemplate[]>([]);
  const [formLateTemplateName, setFormLateTemplateName] = useState<string>('');
  const [formLateTemplateLang, setFormLateTemplateLang] = useState<string>('pt_BR');
  const [formTemplateText, setFormTemplateText] = useState<string>('');
  const [formTemplateVarOrder, setFormTemplateVarOrder] = useState<string[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editFormData, setEditFormData] = useState({
    nome: '',
    randomDelay: 30,
    randomDelayMax: 80 as number | null,
    sessionNames: [] as string[],
    scheduleStartDay: null as number | null,
    scheduleEndDay: null as number | null,
    scheduleStartHour: null as number | null,
    scheduleEndHour: null as number | null,
  });

  // Form states
  const [formData, setFormData] = useState({
    nome: '',
    targetTags: [] as string[],
    targetContactTags: [] as string[],
    sessionNames: [] as string[],
    sessionName: '',
    messageType: 'sequence',
    messageContent: { sequence: [] as Array<{ type: string; content: any }> } as MessageContent,
    randomDelay: 30,
    randomDelayMax: 80 as number | null,
    startImmediately: true,
    scheduledFor: '',
    scheduleStartDay: null as number | null,
    scheduleEndDay: null as number | null,
    scheduleStartHour: null as number | null,
    scheduleEndHour: null as number | null
  });

  // ===========================================================================
  // Mensagem por usuário do rodízio
  // ===========================================================================
  // O editor da etapa 2 é UM só; o que muda é para qual rascunho ele aponta.
  // 'default' = a mensagem padrão da campanha (continua vivendo em
  // formData.messageContent); qualquer outro valor é o id de um usuário do
  // rodízio que escreveu a mensagem dele.
  type MessageDraft = { sequence: Array<{ type: string; content: any }> };
  const [perUserMessages, setPerUserMessages] = useState<Record<string, MessageDraft>>({});
  // Equivalente para campanha oficial: ali o que muda por usuário é o template.
  const [perUserTemplates, setPerUserTemplates] = useState<Record<string, { lateTemplateName: string; lateTemplateLang: string; text: string; variableOrder: string[] }>>({});
  const [activeMessageDraft, setActiveMessageDraft] = useState<string>('default');

  const defaultSequence = ('sequence' in formData.messageContent)
    ? formData.messageContent.sequence
    : ([] as Array<{ type: string; content: any }>);
  // Sequência que o editor lê. Todo o corpo do editor usa esta constante.
  // Rede de segurança: um rascunho que não pertence mais ao rodízio nunca pode
  // ficar ativo — o editor mostraria a mensagem de alguém fora da campanha
  // achando que é a padrão.
  const activeDraftIsValid = activeMessageDraft === 'default'
    || postSendActions.assignToUserIds.includes(activeMessageDraft);
  const activeSequence = !activeDraftIsValid || activeMessageDraft === 'default'
    ? defaultSequence
    : (perUserMessages[activeMessageDraft]?.sequence || []);
  // ...e este setter, que roteia a escrita para o rascunho ativo.
  const setActiveSequence = (next: Array<{ type: string; content: any }>) => {
    if (activeMessageDraft === 'default' || !activeDraftIsValid) {
      setFormData(prev => ({ ...prev, messageContent: { sequence: next } }));
      return;
    }
    const draftKey = activeMessageDraft;
    setPerUserMessages(prev => ({ ...prev, [draftKey]: { sequence: next } }));
  };

  const selectedLateTemplate = lateTemplates.find(template => template.name === formLateTemplateName) || null;
  const selectedTemplateIncompatibleVariables = selectedLateTemplate ? getTemplateIncompatibleVariables(selectedLateTemplate) : [];
  const selectedTemplateUnknownVariables = selectedLateTemplate ? getTemplateUnknownVariables(selectedLateTemplate) : [];

  useEffect(() => {
    loadCampaigns();
    loadContactTags();
    loadContactEtiquetas();
    loadTenantUsers();
    loadWhatsAppSessions();
    loadLateAccounts();
    apiService.getContactLabels().then((labels) => setContactEtiquetaColors(Object.fromEntries(labels.map((label) => [label.name.trim().toLowerCase(), label.color])))).catch(() => setContactEtiquetaColors({}));
  }, []);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadCampaigns = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await authenticatedFetch('/api/campaigns');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast.error('Erro ao carregar campanhas');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const hasActiveAllcanceCampaign = campaigns.some(campaign =>
    (campaign.provider === 'allcance' || campaign.messageType === 'allcance') &&
    ['RUNNING', 'PROCESSING'].includes(campaign.status)
  );

  useEffect(() => {
    if (!hasActiveAllcanceCampaign) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadCampaigns(false);
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [hasActiveAllcanceCampaign]);

  const handleSyncAllcanceStatus = async (campaignId: string) => {
    setSyncingAllcanceId(campaignId);
    try {
      const response = await authenticatedFetch(`/api/campaigns/${campaignId}/sync-allcance`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(providerSafeText(data.error, 'Não foi possível atualizar o status'));
      await loadCampaigns(false);
      toast.success('Status do envio atualizado');
    } catch (error) {
      toast.error(providerSafeText(error instanceof Error ? error.message : '', 'Erro ao atualizar status'));
    } finally {
      setSyncingAllcanceId(null);
    }
  };

  const loadContactTags = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/tags');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const tags = await response.json();
      setContactTags(tags);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadContactEtiquetas = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/etiquetas');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const etiquetas = await response.json();
      setContactEtiquetas(Array.isArray(etiquetas) ? etiquetas : []);
    } catch (error) {
      console.error('Erro ao carregar etiquetas:', error);
    }
  };

  // Usuários do tenant para a atribuição opcional da conversa (etapa 3).
  // Resposta do /api/users vem embrulhada em { success, data: { users, pagination } }.
  // A busca vai ao servidor porque a listagem é paginada em 100: num tenant maior
  // que isso, filtrar só em memória deixaria parte dos usuários inalcançável.
  const loadTenantUsers = async (search = '') => {
    try {
      const params = new URLSearchParams({ pageSize: '100', ativo: 'true' });
      if (search.trim()) params.set('search', search.trim());
      const response = await authenticatedFetch(`/api/users?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      const list: TenantUserOption[] = payload?.data?.users || payload?.users || [];
      setTenantUsers(list.filter((item) => item.ativo !== false));
      const total = Number(payload?.data?.pagination?.total ?? list.length);
      setTenantUsersTruncated(Number.isFinite(total) && total > list.length);
    } catch (error) {
      console.error('Erro ao carregar usuarios:', error);
    }
  };

  const loadWhatsAppSessions = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const sessions = await response.json();
      setWhatsappSessions(sessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  const loadLateAccounts = async () => {
    try {
      const res = await authenticatedFetch('/api/whatsapp-templates/accounts');
      if (res.ok) {
        const data = await res.json();
        const accounts = data.accounts || data.data || data || [];
        setLateAccounts(accounts);
        if (accounts.length > 0 && !formLateAccountId) {
          // platformAccountId é opcional no banco; cair para o id da conexão
          // evita mandar accountId vazio ao backend.
          const defaultAccountId = accounts[0].platformAccountId || accounts[0].id;
          setFormLateAccountId(defaultAccountId || '');
          if (defaultAccountId) {
            loadLateTemplates(defaultAccountId);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar contas WhatsApp Oficial:', error);
    }
  };

  const loadLateTemplates = async (accountId: string) => {
    if (!accountId) { setLateTemplates([]); return; }
    setLoadingTemplates(true);
    try {
      const res = await authenticatedFetch(`/api/whatsapp-templates/templates?accountId=${accountId}`);
      if (res.ok) {
        const data = await res.json();
        setLateTemplates(data.templates || data.data || data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTagToggle = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tag)
        ? prev.targetTags.filter(t => t !== tag)
        : [...prev.targetTags, tag]
    }));
  };

  const handleEtiquetaToggle = (etiqueta: string) => {
    setFormData(prev => ({
      ...prev,
      targetContactTags: prev.targetContactTags.includes(etiqueta)
        ? prev.targetContactTags.filter(t => t !== etiqueta)
        : [...prev.targetContactTags, etiqueta]
    }));
  };

  // Agrupa categorias por funil para exibição (funil = cabeçalho, categorias = clicáveis)
  const groupCategoriesByFunnel = (tags: ContactTag[]) => {
    const groups: Array<{ funnelId: string | null; funnelNome: string; funnelCor: string | null; categories: ContactTag[] }> = [];
    const byFunnel = new Map<string, { funnelId: string | null; funnelNome: string; funnelCor: string | null; categories: ContactTag[] }>();
    for (const tag of tags) {
      const key = tag.funnelId || '__sem_funil__';
      if (!byFunnel.has(key)) {
        const group = {
          funnelId: tag.funnelId || null,
          funnelNome: tag.funnelNome || 'Sem funil',
          funnelCor: tag.funnelCor || null,
          categories: [] as ContactTag[]
        };
        byFunnel.set(key, group);
        groups.push(group);
      }
      byFunnel.get(key)!.categories.push(tag);
    }
    // Grupos com funil primeiro (ordem alfabética), "Sem funil" por último
    groups.sort((a, b) => {
      if (!a.funnelId && b.funnelId) return 1;
      if (a.funnelId && !b.funnelId) return -1;
      return a.funnelNome.localeCompare(b.funnelNome, 'pt-BR', { sensitivity: 'base' });
    });
    return groups;
  };

  const filteredContactTags = contactTags.filter(tag =>
    tag.nome.toLowerCase().includes(categorySearch.trim().toLowerCase())
  );

  const filteredEtiquetas = contactEtiquetas.filter(etiqueta =>
    etiqueta.toLowerCase().includes(etiquetaSearch.trim().toLowerCase())
  );

  // Cor viva da etiqueta (mesma fonte usada na etapa 1).
  const etiquetaColor = (etiqueta: string) =>
    contactEtiquetaColors[etiqueta.trim().toLowerCase()] || '#64748b';

  // Chip de etiqueta com a cor cheia e texto branco — idêntico ao da etapa 1.
  const EtiquetaChip = ({ etiqueta }: { etiqueta: string }) => (
    <span
      className="rounded-full border px-2.5 py-1 text-sm font-medium text-white"
      style={{ backgroundColor: etiquetaColor(etiqueta), borderColor: etiquetaColor(etiqueta) }}
    >
      {etiqueta}
    </span>
  );

  const postSendRemoveOptions = contactEtiquetas.filter(etiqueta =>
    etiqueta.toLowerCase().includes(postSendRemoveSearch.trim().toLowerCase())
  );
  const postSendAddOptions = contactEtiquetas.filter(etiqueta =>
    etiqueta.toLowerCase().includes(postSendAddSearch.trim().toLowerCase())
  );
  const postSendCategoryOptions = contactTags.filter(tag =>
    tag.nome.toLowerCase().includes(postSendCategorySearch.trim().toLowerCase())
    || (tag.funnelNome || '').toLowerCase().includes(postSendCategorySearch.trim().toLowerCase())
  );
  // Filtra o que já está carregado (resposta imediata) enquanto o servidor
  // responde com o resultado completo, caso a lista esteja truncada.
  const postSendUserOptions = tenantUsers.filter(user =>
    (user.nome || '').toLowerCase().includes(postSendUserSearch.trim().toLowerCase())
    || user.email.toLowerCase().includes(postSendUserSearch.trim().toLowerCase())
  );
  // Usuários do rodízio na ordem escolhida (a ordem define quem recebe cada envio).
  const postSendAssignedUsers = postSendActions.assignToUserIds
    .map(id => tenantUsers.find(user => user.id === id) || null)
    .filter((user): user is TenantUserOption => Boolean(user));
  const togglePostSendUser = (userId: string) => {
    const removing = postSendActions.assignToUserIds.includes(userId);
    setPostSendActions(prev => ({
      ...prev,
      assignToUserIds: removing
        ? prev.assignToUserIds.filter(id => id !== userId)
        // Entra no fim: a ordem de seleção é a ordem do rodízio.
        : [...prev.assignToUserIds, userId]
    }));
    if (!removing) return;
    // Tirar o usuário do rodízio tem de tirar junto a aba e o rascunho dele.
    // Sem isto o editor continuava apontando para a mensagem de um usuário que
    // não está mais na campanha — sem aba nenhuma destacada, parecendo a padrão,
    // e tudo o que fosse digitado ali era descartado no envio.
    if (activeMessageDraft === userId) setActiveMessageDraft('default');
    setPerUserMessages(prev => {
      if (!(userId in prev)) return prev;
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    setPerUserTemplates(prev => {
      if (!(userId in prev)) return prev;
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  // Só vai ao servidor quando a lista está truncada (tenant com mais de 100
  // usuários ativos); do contrário o filtro em memória já resolve.
  useEffect(() => {
    if (!tenantUsersTruncated) return;
    const timer = window.setTimeout(() => { loadTenantUsers(postSendUserSearch); }, 350);
    return () => window.clearTimeout(timer);
  }, [postSendUserSearch, tenantUsersTruncated]);

  useEffect(() => {
    if (!showCreateModal) {
      setAudiencePreview(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setAudienceLoading(true);
      try {
        const response = await authenticatedFetch('/api/campaigns/audience-preview', {
          method: 'POST',
          signal: controller.signal,
          body: JSON.stringify({
            targetTags: formData.targetTags,
            targetContactTags: formData.targetContactTags,
            targetFilterMode,
            targetTagMatch,
            withButton: allcanceForm.withButton,
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Não foi possível calcular o público');
        setAudiencePreview(data);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') setAudiencePreview(null);
      } finally {
        if (!controller.signal.aborted) setAudienceLoading(false);
      }
    }, 250);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [showCreateModal, formData.targetTags, formData.targetContactTags, targetFilterMode, targetTagMatch, allcanceForm.withButton]);

  const audienceTotal = audiencePreview?.totalRecipients ?? audiencePreview?.totalContacts ?? 0;
  const audienceTotalLabel = audiencePreview?.totalRecipients !== undefined ? 'destinatários únicos' : 'contatos encontrados';
  const allcanceMinimum = audiencePreview?.allcance?.minimumContacts ?? (allcanceForm.withButton ? 5000 : 1000);
  const allcancePricingKey = allcanceForm.withButton ? 'allcance_whatsapp_button' : 'allcance_whatsapp';
  const allcanceStandardPrice = getCreditPrice('allcance_whatsapp')?.cost ?? 0.34;
  const allcanceButtonPrice = getCreditPrice('allcance_whatsapp_button')?.cost ?? 0.50;
  const allcanceUnitPrice = audiencePreview?.allcance?.unitCost ?? getCreditPrice(allcancePricingKey)?.cost ?? (allcanceForm.withButton ? allcanceButtonPrice : allcanceStandardPrice);
  const allcanceEstimatedCost = audiencePreview?.allcance?.estimatedCredits ?? audienceTotal * allcanceUnitPrice;
  const selectedCategoryNames = contactTags.filter(tag => formData.targetTags.includes(tag.id)).map(tag => tag.nome);
  const audienceSummary = (() => {
    const categories = selectedCategoryNames.length ? `${selectedCategoryNames.length} categoria(s)` : '';
    const tagDescription = formData.targetContactTags.length
      ? `${formData.targetContactTags.length} etiqueta(s), ${targetTagMatch === 'all' ? 'todas ao mesmo tempo' : 'qualquer uma'}`
      : '';
    if (categories && tagDescription) {
      return targetFilterMode === 'intersection'
        ? `Contatos das ${categories} que também tenham ${tagDescription}.`
        : `Contatos das ${categories} ou que tenham ${tagDescription}.`;
    }
    if (categories) return `Todos os contatos das ${categories}.`;
    if (tagDescription) return `Contatos que tenham ${tagDescription}.`;
    return 'Selecione ao menos uma categoria ou etiqueta.';
  })();

  const handleAllcanceProfilePhoto = (file?: File) => {
    if (!file) {
      setAllcanceForm(prev => ({ ...prev, profilePhotoBase64: '' }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem para a foto do perfil');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAllcanceForm(prev => ({ ...prev, profilePhotoBase64: String(reader.result || '') }));
    reader.onerror = () => toast.error('Não foi possível ler a foto do perfil');
    reader.readAsDataURL(file);
  };

  const goToNextWizardStep = () => {
    if (wizardStep === 1) {
      if (!formData.nome.trim()) { toast.error('Informe o nome da campanha'); return; }
      if (formData.targetTags.length === 0 && formData.targetContactTags.length === 0) {
        toast.error('Selecione pelo menos uma categoria ou etiqueta');
        return;
      }
      if (audienceLoading) { toast.error('Aguarde o cálculo do público'); return; }
      if (audiencePreview && audienceTotal === 0) { toast.error('Os filtros selecionados não encontraram contatos'); return; }
      setWizardStep(2);
      return;
    }
    if (wizardStep === 2) {
      if (deliveryProvider === 'allcance') {
        if (audienceTotal < allcanceMinimum) {
          toast.error(`Este envio exige no mínimo ${allcanceMinimum.toLocaleString('pt-BR')} destinatários únicos`);
          return;
        }
        if (!allcanceForm.profileName.trim() || !allcanceForm.text.trim()) {
          toast.error('Preencha o perfil remetente e a mensagem');
          return;
        }
        if (allcanceForm.withButton && allcanceForm.text.length > 500) {
          toast.error('Campanhas com botão aceitam até 500 caracteres');
          return;
        }
        if (allcanceForm.withButton && (!allcanceForm.buttonText.trim() || !/^https?:\/\//i.test(allcanceForm.buttonUrl))) {
          toast.error('Preencha o botão e use uma URL http/https válida');
          return;
        }
      } else if (deliveryProvider === 'official') {
        if (!formLateAccountId || !formLateTemplateName || !formTemplateText.trim()) {
          toast.error('Selecione a conta e um template oficial aprovado');
          return;
        }
      } else {
        if (formData.sessionNames.length === 0) { toast.error('Selecione pelo menos uma conexão WhatsApp'); return; }
        // A mensagem padrão é obrigatória mesmo com mensagens por usuário: ela é o
        // que dispara para quem não tem a própria (e para o rodízio inteiro, se
        // ninguém personalizar).
        if (defaultSequence.length === 0) { toast.error('Adicione pelo menos uma mensagem'); return; }
      }
      setWizardStep(3);
      return;
    }
    if (wizardStep === 3) {
      // Etapa opcional: seguir sem configurar nada é válido (nada muda no contato).
      setWizardStep(4);
    }
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter(s => s !== sessionName)
        : [...prev.sessionNames, sessionName]
    }));
  };

  const handleSelectAllTags = () => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.length === contactTags.length
        ? []
        : contactTags.map(tag => tag.id)
    }));
  };

  const handleSelectAllSessions = () => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.length === whatsappSessions.length
        ? []
        : whatsappSessions.map(session => session.name)
    }));
  };

  // Itens de voz clonada de TODAS as mensagens (a padrão e as próprias de cada
  // usuário do rodízio) — o orçamento e as validações precisam ver todas, não só
  // a que está aberta no editor.
  const allMessageDrafts = (): Array<Array<{ type: string; content: any }>> => {
    const drafts = [defaultSequence];
    for (const userId of postSendActions.assignToUserIds) {
      const sequence = perUserMessages[userId]?.sequence;
      if (sequence && sequence.length > 0) drafts.push(sequence);
    }
    return drafts;
  };

  // `all: false` = só a mensagem padrão. É o que vai para o ORÇAMENTO: o
  // endpoint multiplica cada item pelo público inteiro, mas a mensagem de um
  // usuário do rodízio só atende 1/N dos contatos — somar todos os rascunhos
  // inflava o custo em ~N vezes. `all: true` é para as VALIDAÇÕES, que precisam
  // olhar todas as mensagens (voz sem voiceCloneId em qualquer uma delas quebra
  // o disparo daquele usuário).
  const voiceItemsInForm = (options: { all?: boolean } = {}): Array<{ type: string; voiceCloneId: string; text: string }> => {
    if (useOfficialApi) return [];
    const source = options.all ? allMessageDrafts().flat() : defaultSequence;
    return source
      .filter((item) => item.type === 'voice_clone')
      .map((item) => ({ type: 'voice_clone', voiceCloneId: item.content?.voiceCloneId || '', text: item.content?.text || '' }));
  };

  // Passo extra antes de criar: com voz clonada, o custo depende do público e
  // do texto de CADA contato (uma observação longa vira um áudio bem maior),
  // então o servidor resolve a lista real e devolve o total antes de gravar.
  const requestVoiceEstimate = async () => {
    setVoiceEstimateOpen(true);
    setVoiceEstimateLoading(true);
    setVoiceEstimate(null);
    try {
      const response = await authenticatedFetch('/api/campaigns/estimate-voice', {
        method: 'POST',
        body: JSON.stringify({
          targetTags: formData.targetTags,
          targetContactTags: formData.targetContactTags,
          items: voiceItemsInForm(),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao calcular o custo');
      if (!data.data) {
        // Sem público ou sem texto: segue direto, o backend valida na criação.
        setVoiceEstimateOpen(false);
        return submitCampaign();
      }
      setVoiceEstimate(data.data);
    } catch (error) {
      setVoiceEstimateOpen(false);
      toast.error(error instanceof Error ? error.message : 'Erro ao calcular o custo do disparo');
    } finally {
      setVoiceEstimateLoading(false);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.targetTags.length === 0 && formData.targetContactTags.length === 0) {
      toast.error('Selecione pelo menos uma categoria ou etiqueta de contatos');
      return;
    }

    if (!formData.nome.trim()) {
      toast.error('Informe o nome da campanha');
      return;
    }

    if (deliveryProvider === 'allcance') {
      if (audienceLoading) { toast.error('Aguarde o cálculo do público'); return; }
      if (audienceTotal < allcanceMinimum) {
        toast.error(`Este envio exige no mínimo ${allcanceMinimum.toLocaleString('pt-BR')} destinatários únicos`);
        return;
      }
      if (!allcanceForm.profileName.trim()) { toast.error('Informe o nome do perfil remetente'); return; }
      if (!allcanceForm.text.trim()) { toast.error('Escreva a mensagem da campanha'); return; }
      if (allcanceForm.withButton && allcanceForm.text.length > 500) {
        toast.error('Envios por número de terceiros com botão aceitam até 500 caracteres');
        return;
      }
      if (allcanceForm.withButton && (!allcanceForm.buttonText.trim() || !/^https?:\/\//i.test(allcanceForm.buttonUrl))) {
        toast.error('Informe o texto e uma URL válida (http/https) para o botão');
        return;
      }
    } else if (useOfficialApi) {
      if (!formLateAccountId) { toast.error('Selecione uma conta WhatsApp Oficial'); return; }
      if (!formLateTemplateName) { toast.error('Selecione um template'); return; }
      if (!formTemplateText.trim()) { toast.error('O corpo do template não pode estar vazio'); return; }
      if (!selectedLateTemplate || !isTemplateCompatibleWithCampaign(selectedLateTemplate)) {
        toast.error('Selecione um template compatível com campanhas de contato');
        return;
      }
    } else {
      if (formData.sessionNames.length === 0) {
        toast.error('Selecione pelo menos uma conexão WhatsApp');
        return;
      }
      if (defaultSequence.length === 0) {
        toast.error('Adicione pelo menos uma mensagem à campanha');
        return;
      }
      // Validação olha TODAS as mensagens (a padrão e as por usuário).
      const voiceItems = voiceItemsInForm({ all: true });
      if (voiceItems.some((item) => !item.voiceCloneId)) {
        toast.error('Selecione a voz clonada de cada mensagem de voz');
        return;
      }
      if (voiceItems.some((item) => !item.text.trim())) {
        toast.error('Escreva o texto de cada mensagem de voz clonada');
        return;
      }
      // Com voz clonada, o custo só é conhecido depois de resolver o público:
      // mostra o total real e só grava a campanha se o usuário confirmar.
      if (voiceItems.length > 0) return requestVoiceEstimate();
    }

    return submitCampaign();
  };

  const submitCampaign = async () => {
    try {
      let finalMessageType: string;
      let finalMessageContent: any;

      if (deliveryProvider === 'allcance') {
        finalMessageType = 'allcance';
        finalMessageContent = {
          text: allcanceForm.text.trim(),
          profileName: allcanceForm.profileName.trim(),
          profilePhotoBase64: allcanceForm.profilePhotoBase64 || undefined,
          withButton: allcanceForm.withButton,
          buttonText: allcanceForm.withButton ? allcanceForm.buttonText.trim() : undefined,
          buttonUrl: allcanceForm.withButton ? allcanceForm.buttonUrl.trim() : undefined,
        };
      } else if (useOfficialApi) {
        finalMessageType = 'whatsapp_template';
        finalMessageContent = { text: formTemplateText, variableOrder: formTemplateVarOrder };
      } else {
        const sequence = defaultSequence;
        const isSequence = sequence.length > 1;
        if (isSequence) {
          finalMessageType = 'sequence';
          finalMessageContent = { sequence };
        } else {
          const singleMessage = sequence[0];
          finalMessageType = singleMessage.type;
          finalMessageContent = singleMessage.content;
        }
      }

      // Mensagem própria por usuário do rodízio. Só entra quem está no rodízio e
      // tem conteúdo de verdade — vazio significa "usa a padrão". Diferente da
      // mensagem padrão acima, a sequência NÃO é achatada quando tem um item só:
      // o backend guarda sempre { sequence: [...] } aqui.
      const finalPerUserMessages: Record<string, any> = {};
      for (const userId of postSendActions.assignToUserIds) {
        if (useOfficialApi) {
          const template = perUserTemplates[userId];
          if (!template?.lateTemplateName) continue;
          finalPerUserMessages[userId] = {
            messageType: 'whatsapp_template',
            lateTemplateName: template.lateTemplateName,
            lateTemplateLang: template.lateTemplateLang || null,
            messageContent: { text: template.text, variableOrder: template.variableOrder }
          };
          continue;
        }
        const sequence = perUserMessages[userId]?.sequence || [];
        if (sequence.length === 0) continue;
        finalPerUserMessages[userId] = { messageType: 'sequence', messageContent: { sequence } };
      }

      // Converter scheduledFor de datetime-local para ISO string
      let scheduledForISO = null;
      if (!formData.startImmediately && formData.scheduledFor) {
        // datetime-local retorna formato "YYYY-MM-DDTHH:mm" SEM timezone
        // Precisamos interpretar como horário LOCAL do usuário e converter para UTC
        // Exemplo: usuário escolhe 2025-10-15T19:00 em UTC-3 (Brasília)
        // Isso deve ser salvo como 2025-10-15T22:00:00.000Z (19:00 - 3 = 22:00 UTC)
        const localDateStr = formData.scheduledFor; // "2025-10-15T19:00"
        const localDate = new Date(localDateStr); // JavaScript interpreta como LOCAL
        scheduledForISO = localDate.toISOString(); // Converte para UTC com timezone
      }

      const campaignData: any = {
        ...formData,
        targetFilterMode,
        targetTagMatch,
        postSendActions,
        perUserMessages: Object.keys(finalPerUserMessages).length > 0 ? finalPerUserMessages : undefined,
        provider: deliveryProvider === 'allcance' ? 'allcance' : undefined,
        channel: deliveryProvider === 'allcance' ? 'third_party_number' : undefined,
        sessionNames: useOfficialApi || deliveryProvider === 'allcance' ? [] : formData.sessionNames,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        scheduledFor: scheduledForISO,
        randomDelay: deliveryProvider === 'allcance' ? 0 : formData.randomDelay,
        randomDelayMax: deliveryProvider === 'allcance' ? 0 : formData.randomDelayMax,
        scheduleStartDay: deliveryProvider === 'allcance' ? null : formData.scheduleStartDay,
        scheduleEndDay: deliveryProvider === 'allcance' ? null : formData.scheduleEndDay,
        scheduleStartHour: deliveryProvider === 'allcance' ? null : formData.scheduleStartHour,
        scheduleEndHour: deliveryProvider === 'allcance' ? null : formData.scheduleEndHour,
        lateAccountId: useOfficialApi ? formLateAccountId : null,
        lateTemplateName: useOfficialApi ? formLateTemplateName : null,
        lateTemplateLang: useOfficialApi ? formLateTemplateLang : null,
      };

      const response = await authenticatedFetch('/api/campaigns', {
        method: 'POST',
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.errors && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map((err: any) => err.msg).join(', ');
          throw new Error(validationErrors);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      toast.success('Campanha criada com sucesso!');
      setVoiceEstimateOpen(false);
      setVoiceEstimate(null);
      setShowCreateModal(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error(providerSafeText(error instanceof Error ? error.message : '', 'Erro ao criar campanha'));
    }
  };

  const resetForm = () => {
    setWizardStep(1);
    setPostSendActions({ removeAllTags: false, removeTags: [], addTags: [], moveToCategoriaId: null, assignToUserIds: [] });
    setNewPostSendTag('');
    setPostSendUserSearch('');
    // Rascunhos por usuário: sem isto, a mensagem que a Miriele tinha na campanha
    // anterior reapareceria na próxima.
    setPerUserMessages({});
    setPerUserTemplates({});
    setActiveMessageDraft('default');
    setDeliveryProvider('unofficial');
    setTargetFilterMode('union');
    setTargetTagMatch('any');
    setAudiencePreview(null);
    setAllcanceForm({
      text: '',
      profileName: '',
      profilePhotoBase64: '',
      withButton: false,
      buttonText: '',
      buttonUrl: '',
    });
    setFormData({
      nome: '',
      targetTags: [],
      targetContactTags: [],
      sessionNames: [],
      sessionName: '',
      messageType: 'sequence',
      messageContent: { sequence: [] },
      randomDelay: 30,
      randomDelayMax: 80,
      startImmediately: true,
      scheduledFor: '',
      scheduleStartDay: null,
      scheduleEndDay: null,
      scheduleStartHour: null,
      scheduleEndHour: null
    });
    setUseOfficialApi(false);
    setFormLateAccountId('');
    setFormLateTemplateName('');
    setFormLateTemplateLang('pt_BR');
    setFormTemplateText('');
    setFormTemplateVarOrder([]);
    setLateTemplates([]);
    setUploadingFiles({});
    setFileInfos({});
    setDraggedIndex(null);
    setCategorySearch('');
    setEtiquetaSearch('');
    setCloneSourceName(null);
  };

  const toLocalDateTimeInput = (dateValue: string | null) => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) return '';

    const offsetInMs = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offsetInMs).toISOString().slice(0, 16);
  };

  const handleOpenNewCampaign = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const handleCloneCampaign = (campaign: Campaign) => {
    const clonedMessageContent = JSON.parse(JSON.stringify(campaign.messageContent || {}));
    const isOfficial = isOfficialCampaign(campaign);
    const isAllcance = campaign.messageType === 'allcance' || campaign.provider === 'allcance';
    const sequence = isOfficial || isAllcance
      ? []
      : campaign.messageType === 'sequence' && 'sequence' in clonedMessageContent
        ? clonedMessageContent.sequence
        : [{ type: campaign.messageType, content: clonedMessageContent }];
    const scheduledFor = campaign.startImmediately ? '' : toLocalDateTimeInput(campaign.scheduledFor);

    setFormData({
      nome: `Cópia de ${campaign.nome}`,
      targetTags: [...(campaign.targetTags || [])],
      targetContactTags: [...(campaign.targetContactTags || [])],
      sessionNames: isOfficial || isAllcance ? [] : [...(campaign.sessionNames || [])],
      sessionName: isOfficial || isAllcance ? '' : campaign.sessionName || campaign.sessionNames?.[0] || '',
      messageType: 'sequence',
      messageContent: { sequence },
      randomDelay: campaign.randomDelay,
      randomDelayMax: campaign.randomDelayMax,
      startImmediately: campaign.startImmediately,
      scheduledFor,
      scheduleStartDay: campaign.scheduleStartDay,
      scheduleEndDay: campaign.scheduleEndDay,
      scheduleStartHour: campaign.scheduleStartHour,
      scheduleEndHour: campaign.scheduleEndHour
    });

    setUseOfficialApi(isOfficial);
    setDeliveryProvider(isAllcance ? 'allcance' : isOfficial ? 'official' : 'unofficial');
    setTargetFilterMode(campaign.targetFilterMode || 'union');
    setTargetTagMatch(campaign.targetTagMatch || 'any');
    // Clone nao chama resetForm: sem copiar isto explicitamente, as acoes
    // pos-envio da ULTIMA campanha montada nesta sessao vazavam para a copia.
    // assignToUserId (singular) é o formato antigo, de campanhas criadas antes do
    // rodízio: continua sendo lido para o clone não perder a atribuição.
    const clonedActions: (Partial<PostSendActions> & { assignToUserId?: string | null }) | null =
      campaign.postSendActions || null;
    setPostSendActions({
      removeAllTags: clonedActions?.removeAllTags === true,
      removeTags: Array.isArray(clonedActions?.removeTags) ? [...clonedActions.removeTags] : [],
      addTags: Array.isArray(clonedActions?.addTags) ? [...clonedActions.addTags] : [],
      moveToCategoriaId: clonedActions?.moveToCategoriaId || null,
      assignToUserIds: Array.isArray(clonedActions?.assignToUserIds)
        ? [...clonedActions.assignToUserIds]
        : (clonedActions?.assignToUserId ? [clonedActions.assignToUserId] : [])
    });
    setNewPostSendTag('');
    setPostSendUserSearch('');
    // Mensagens por usuário do rodízio acompanham o clone. O clone não passa por
    // resetForm, então sem preencher aqui os rascunhos do formulário anterior
    // vazariam para a cópia.
    const clonedPerUser: Record<string, any> = (campaign.perUserMessages && typeof campaign.perUserMessages === 'object')
      ? campaign.perUserMessages
      : {};
    const nextPerUserMessages: Record<string, MessageDraft> = {};
    const nextPerUserTemplates: Record<string, { lateTemplateName: string; lateTemplateLang: string; text: string; variableOrder: string[] }> = {};
    for (const [userId, entry] of Object.entries(clonedPerUser)) {
      if (entry?.messageType === 'whatsapp_template') {
        nextPerUserTemplates[userId] = {
          lateTemplateName: entry.lateTemplateName || '',
          lateTemplateLang: entry.lateTemplateLang || '',
          text: entry.messageContent?.text || '',
          variableOrder: Array.isArray(entry.messageContent?.variableOrder) ? [...entry.messageContent.variableOrder] : []
        };
      } else if (Array.isArray(entry?.messageContent?.sequence)) {
        nextPerUserMessages[userId] = { sequence: JSON.parse(JSON.stringify(entry.messageContent.sequence)) };
      }
    }
    setPerUserMessages(nextPerUserMessages);
    setPerUserTemplates(nextPerUserTemplates);
    setActiveMessageDraft('default');
    if (isAllcance) {
      setAllcanceForm({
        text: clonedMessageContent.text || '',
        profileName: clonedMessageContent.profileName || '',
        profilePhotoBase64: clonedMessageContent.profilePhotoBase64 || '',
        withButton: !!clonedMessageContent.withButton,
        buttonText: clonedMessageContent.buttonText || '',
        buttonUrl: clonedMessageContent.buttonUrl || '',
      });
    }
    setCloneSourceName(campaign.nome);
    setUploadingFiles({});
    setFileInfos({});
    setDraggedIndex(null);
    setCategorySearch('');
    setEtiquetaSearch('');

    if (isOfficial) {
      const templateContent = clonedMessageContent as { text?: string; variableOrder?: string[] };
      const accountId = campaign.lateAccountId || '';
      setFormLateAccountId(accountId);
      setFormLateTemplateName(campaign.lateTemplateName || '');
      setFormLateTemplateLang(campaign.lateTemplateLang || 'pt_BR');
      setFormTemplateText(templateContent.text || '');
      setFormTemplateVarOrder(templateContent.variableOrder || extractVariableOrder(templateContent.text || ''));
      setLateTemplates([]);
      if (accountId) loadLateTemplates(accountId);
    } else {
      setFormLateAccountId('');
      setFormLateTemplateName('');
      setFormLateTemplateLang('pt_BR');
      setFormTemplateText('');
      setFormTemplateVarOrder([]);
      setLateTemplates([]);
    }

    if (!campaign.startImmediately && !scheduledFor) {
      toast('O agendamento original venceu. Escolha uma nova data antes de criar a cópia.');
    }

    setShowCreateModal(true);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const currentSequence = activeSequence;
    const newSequence = [...currentSequence];
    const [draggedItem] = newSequence.splice(draggedIndex, 1);
    newSequence.splice(dropIndex, 0, draggedItem);

    setActiveSequence(newSequence);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleFileUpload = async (file: File, messageIndex: number, variationIndex?: number) => {
    // A chave inclui o rascunho: os cards sao renderizados por INDICE, e sem o
    // prefixo o arquivo do item 0 da mensagem padrao aparecia (e era removido)
    // no item 0 da mensagem de outro usuario do rodizio.
    const uploadKey = variationIndex !== undefined ? `${activeMessageDraft}:${messageIndex}-${variationIndex}` : `${activeMessageDraft}:${messageIndex}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      // Armazenar informações do arquivo
      if (variationIndex !== undefined) {
        setFileInfos(prev => ({
          ...prev,
          [`${activeMessageDraft}:${messageIndex}-${variationIndex}`]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      } else {
        setFileInfos(prev => ({
          ...prev,
          [`${activeMessageDraft}:${messageIndex}`]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      }

      const currentSequence = activeSequence;

      if (variationIndex !== undefined) {
        // Upload para variação
        const newSequence = currentSequence.map((seqItem, i) => {
          if (i === messageIndex) {
            const mediaVariations = [...(seqItem.content.mediaVariations || [])];
            // Garantir que o array tenha o tamanho necessário
            while (mediaVariations.length <= variationIndex) {
              mediaVariations.push({ url: '', caption: '', fileName: '' });
            }
            mediaVariations[variationIndex] = {
              ...mediaVariations[variationIndex],
              url: data.fileUrl,
              fileName: data.originalName
            };
            return {
              ...seqItem,
              content: { ...seqItem.content, mediaVariations }
            };
          }
          return seqItem;
        });

        setActiveSequence(newSequence);
      } else {
        // Upload para modo single
        const newSequence = currentSequence.map((seqItem, i) =>
          i === messageIndex ? {
            ...seqItem,
            content: { ...seqItem.content, url: data.fileUrl }
          } : seqItem
        );

        setActiveSequence(newSequence);
      }

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleRemoveFile = (messageIndex: number) => {
    const currentSequence = activeSequence;
    const newSequence = currentSequence.map((seqItem, i) =>
      i === messageIndex ? {
        ...seqItem,
        content: { ...seqItem.content, url: '' }
      } : seqItem
    );

    setActiveSequence(newSequence);

    setFileInfos(prev => {
      const updated = { ...prev };
      delete updated[`${activeMessageDraft}:${messageIndex}`];
      return updated;
    });

    toast.success('Arquivo removido');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    return '📄';
  };

  const handleToggleCampaign = async (campaignId: string, action: 'pause' | 'resume') => {
    try {
      const response = await authenticatedFetch(`/api/campaigns/${campaignId}/toggle`, {
        method: 'PATCH',
        body: JSON.stringify({ action })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success(`Campanha ${action === 'pause' ? 'pausada' : 'retomada'} com sucesso!`);
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao alterar status da campanha:', error);
      toast.error('Erro ao alterar status da campanha');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta campanha?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/campaigns/${campaignId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success('Campanha excluída com sucesso!');
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao excluir campanha:', error);
      toast.error('Erro ao excluir campanha');
    }
  };

  const handleOpenEditModal = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setEditFormData({
      nome: campaign.nome,
      randomDelay: campaign.randomDelay,
      randomDelayMax: campaign.randomDelayMax,
      sessionNames: campaign.sessionNames || [],
      scheduleStartDay: campaign.scheduleStartDay,
      scheduleEndDay: campaign.scheduleEndDay,
      scheduleStartHour: campaign.scheduleStartHour,
      scheduleEndHour: campaign.scheduleEndHour,
    });
    setShowEditModal(true);
  };

  const handleEditSessionToggle = (sessionName: string) => {
    setEditFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter(s => s !== sessionName)
        : [...prev.sessionNames, sessionName]
    }));
  };

  const handleEditSelectAllSessions = () => {
    setEditFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.length === whatsappSessions.length
        ? []
        : whatsappSessions.map(session => session.name)
    }));
  };

  const handleSaveEditCampaign = async () => {
    if (!editingCampaign) return;

    if (!isOfficialCampaign(editingCampaign) && editFormData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma conexão WhatsApp');
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/campaigns/${editingCampaign.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nome: editFormData.nome,
          randomDelay: editFormData.randomDelay,
          randomDelayMax: editFormData.randomDelayMax,
          sessionNames: isOfficialCampaign(editingCampaign) ? [] : editFormData.sessionNames,
          scheduleStartDay: editFormData.scheduleStartDay,
          scheduleEndDay: editFormData.scheduleEndDay,
          scheduleStartHour: editFormData.scheduleStartHour,
          scheduleEndHour: editFormData.scheduleEndHour,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Campanha atualizada com sucesso!');
      setShowEditModal(false);
      setEditingCampaign(null);
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao atualizar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar campanha');
    }
  };

  const handleViewReport = async (campaignId: string) => {
    setCurrentReportCampaignId(campaignId);
    setReportCurrentPage(1);
    setReportLoading(true);
    setShowReportModal(true);

    try {
      const response = await authenticatedFetch(`/api/campaigns/${campaignId}/report`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
        setShowReportModal(false);
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
      setShowReportModal(false);
    } finally {
      setReportLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gray-100 text-gray-800';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Aguardando';
      case 'RUNNING':
        return 'Executando';
      case 'COMPLETED':
        return 'Concluída';
      case 'PAUSED':
        return 'Pausada';
      case 'FAILED':
        return 'Falha';
      default:
        return status;
    }
  };

  const handleRefreshReport = async () => {
    if (!currentReportCampaignId) return;

    setReportLoading(true);
    try {
      const response = await authenticatedFetch(`/api/campaigns/${currentReportCampaignId}/report`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!currentReportCampaignId || !reportData) return;

    try {
      const response = await authenticatedFetch(`/api/campaigns/${currentReportCampaignId}/report/download`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `relatorio-campanha-${reportData.campaign.nome}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Relatório baixado com sucesso!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao baixar relatório');
      }
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error('Erro ao baixar relatório');
    }
  };

  const getErrorIcon = (errorMessage: string) => {
    if (!errorMessage || errorMessage === 'N/A') return null;

    const error = errorMessage.toLowerCase();

    if (error.includes('network') || error.includes('connection') || error.includes('timeout')) {
      return {
        icon: '🌐',
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
        category: 'Conectividade'
      };
    }

    if (error.includes('blocked') || error.includes('banned') || error.includes('spam')) {
      return {
        icon: '🚫',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        category: 'Bloqueio'
      };
    }

    if (error.includes('rate limit') || error.includes('quota') || error.includes('limit')) {
      return {
        icon: '⏰',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        category: 'Rate Limit'
      };
    }

    if (error.includes('invalid') || error.includes('not found') || error.includes('number')) {
      return {
        icon: '📱',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        category: 'Número Inválido'
      };
    }

    if (error.includes('permission') || error.includes('unauthorized') || error.includes('forbidden')) {
      return {
        icon: '🔒',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        category: 'Permissão'
      };
    }

    if (error.includes('session') || error.includes('disconnected') || error.includes('logout')) {
      return {
        icon: '📲',
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100',
        category: 'Sessão'
      };
    }

    if (error.includes('media') || error.includes('file') || error.includes('download')) {
      return {
        icon: '📁',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        category: 'Mídia'
      };
    }

    // Erro genérico
    return {
      icon: '⚠️',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      category: 'Erro Geral'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando campanhas...</span>
      </div>
    );
  }

  return (
    <>
      {voiceEstimateOpen && (
        // Portal: o modal de criação também é portalado no body. Renderizar aqui
        // dentro deixaria este modal preso no contexto de empilhamento da página.
        <Portal>
          <VoiceCampaignCostModal
            estimate={voiceEstimate}
            loading={voiceEstimateLoading}
            onConfirm={submitCampaign}
            onCancel={() => { setVoiceEstimateOpen(false); setVoiceEstimate(null); }}
          />
        </Portal>
      )}
      <Header
        title="Campanhas"
        subtitle={`${campaigns.length} campanhas ativas`}
        actions={
          <button
            onClick={handleOpenNewCampaign}
            className="btn-primary"
          >
            + Nova Campanha
          </button>
        }
      />

      <div className="p-4 sm:p-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Campanhas Criadas</h3>
          </div>

          {campaigns.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nenhuma campanha encontrada. Crie sua primeira campanha para começar.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className={`px-4 sm:px-6 py-3 flex items-center ${(campaign.provider === 'allcance' || campaign.messageType === 'allcance') ? 'md:min-h-[72px]' : 'md:h-[60px]'}`}>
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between w-full">
                    {/* Nome e Status */}
                    <div className="flex items-center gap-3 min-w-0 md:flex-1">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{campaign.nome}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                        {getStatusText(campaign.status)}
                      </span>
                      {isAllcanceCampaign(campaign) && (
                        <span
                          className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-800"
                          title="Status informado pelo provedor"
                        >
                          Número de terceiros: {providerSafeText(campaign.providerStatus, 'aguardando atualização').replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>

                    {/* Informações essenciais */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Contatos:</span>
                        <span>{campaign.totalContacts}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Enviados:</span>
                        <span>{campaign.sentCount}/{campaign.totalContacts}</span>
                      </div>

                      {/* Sessões ativas (apenas ícones) */}
                      {campaign.sessionNames && campaign.sessionNames.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Sessões:</span>
                          <div className="flex gap-1">
                            {campaign.sessionNames.slice(0, 3).map((sessionName, index) => (
                              <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                {sessionName.substring(0, 3)}
                              </span>
                            ))}
                            {campaign.sessionNames.length > 3 && (
                              <span className="text-xs text-gray-500">+{campaign.sessionNames.length - 3}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Botões de ação */}
                    <div className="flex flex-wrap gap-1 md:ml-4 shrink-0">
                      {isAllcanceCampaign(campaign) && campaign.externalReference && (
                        <button
                          type="button"
                          onClick={() => handleSyncAllcanceStatus(campaign.id)}
                          disabled={syncingAllcanceId === campaign.id}
                          className="rounded bg-violet-600 px-2 py-1 text-xs text-white hover:bg-violet-700 disabled:cursor-wait disabled:opacity-60"
                          title="Consultar o status mais recente no backend"
                        >
                          {syncingAllcanceId === campaign.id ? 'Atualizando…' : 'Atualizar status'}
                        </button>
                      )}
                      <button
                        onClick={() => handleViewReport(campaign.id)}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        title="Ver relatório da campanha"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                        </svg>
                      </button>
                      {!isLockedAllcanceCampaign(campaign) && (
                        <button
                          onClick={() => handleOpenEditModal(campaign)}
                          className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
                          title="Editar campanha"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleCloneCampaign(campaign)}
                        className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                        title="Clonar campanha"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                          <rect x="6" y="6" width="10" height="10" rx="1" strokeWidth="1.8" />
                          <path d="M4 13H3.5A1.5 1.5 0 012 11.5v-8A1.5 1.5 0 013.5 2h8A1.5 1.5 0 0113 3.5V4" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </button>
                      {campaign.status === 'RUNNING' && !isLockedAllcanceCampaign(campaign) && (
                        <button
                          onClick={() => handleToggleCampaign(campaign.id, 'pause')}
                          className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                          title="Pausar campanha"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      {campaign.status === 'PAUSED' && !isLockedAllcanceCampaign(campaign) && (
                        <button
                          onClick={() => handleToggleCampaign(campaign.id, 'resume')}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                          title="Retomar campanha"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      {campaign.status !== 'RUNNING' && !isLockedAllcanceCampaign(campaign) && (
                        <button
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                          title="Excluir campanha"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal de Criação */}
        {showCreateModal && (
          <Portal>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] max-h-[95dvh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{cloneSourceName ? 'Clonar Campanha' : 'Nova Campanha'}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {cloneSourceName ? `Edite a cópia de "${cloneSourceName}" antes de criar` : 'Configure sua campanha de mensagens WhatsApp'}
                  </p>
                </div>
                <button
                  onClick={handleCloseCreateModal}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateCampaign} className="p-4 sm:p-6">
                <div className="mb-6 grid grid-cols-4 gap-2">
                  {[
                    { step: 1, title: 'Público', subtitle: 'Nome e contatos' },
                    { step: 2, title: 'Mensagem', subtitle: 'Canal e conteúdo' },
                    { step: 3, title: 'Etiquetas e funil', subtitle: 'O que muda no contato' },
                    { step: 4, title: 'Envio', subtitle: 'Quando disparar' },
                  ].map(item => (
                    <button
                      key={item.step}
                      type="button"
                      onClick={() => item.step < wizardStep && setWizardStep(item.step as WizardStep)}
                      className={`rounded-xl border px-2 py-3 text-left transition ${
                        wizardStep === item.step
                          ? 'border-blue-500 bg-blue-50 text-blue-800'
                          : item.step < wizardStep
                            ? 'border-green-200 bg-green-50 text-green-800'
                            : 'border-gray-200 bg-gray-50 text-gray-500'
                      }`}
                    >
                      <span className="mb-1 block text-xs font-bold">{item.step < wizardStep ? '✓' : item.step} · {item.title}</span>
                      <span className="hidden text-[11px] sm:block">{item.subtitle}</span>
                    </button>
                  ))}
                </div>

                <div className={`grid grid-cols-1 gap-8 ${wizardStep === 2 && deliveryProvider !== 'allcance' ? 'lg:grid-cols-2' : ''}`}>

                  {/* COLUNA ESQUERDA - Informações Básicas */}
                  <div className="space-y-6">
                    {wizardStep === 1 && (<>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h4 className="text-lg font-semibold text-blue-900 mb-2">Informações Básicas</h4>
                      <p className="text-sm text-blue-700">Configure os dados fundamentais da sua campanha</p>
                    </div>

                    {/* Nome da Campanha */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome da Campanha *
                      </label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Promoção Black Friday 2024"
                        required
                      />
                    </div>

                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                      <label className="block text-sm font-semibold text-blue-950">Como combinar categoria e etiqueta?</label>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <label className={`cursor-pointer rounded-lg border p-3 ${targetFilterMode === 'union' ? 'border-blue-500 bg-white' : 'border-blue-200'}`}>
                          <input type="radio" className="mr-2" checked={targetFilterMode === 'union'} onChange={() => setTargetFilterMode('union')} />
                          <span className="text-sm font-medium">Ampliar público (OU)</span>
                          <span className="mt-1 block pl-5 text-xs text-gray-600">Está na categoria ou possui a etiqueta.</span>
                        </label>
                        <label className={`cursor-pointer rounded-lg border p-3 ${targetFilterMode === 'intersection' ? 'border-blue-500 bg-white' : 'border-blue-200'}`}>
                          <input type="radio" className="mr-2" checked={targetFilterMode === 'intersection'} onChange={() => setTargetFilterMode('intersection')} />
                          <span className="text-sm font-medium">Refinar público (E)</span>
                          <span className="mt-1 block pl-5 text-xs text-gray-600">Está na categoria e também possui a etiqueta.</span>
                        </label>
                      </div>
                      {formData.targetContactTags.length > 1 && (
                        <div className="mt-3 border-t border-blue-200 pt-3">
                          <span className="text-xs font-semibold text-gray-700">Para várias etiquetas, o contato precisa ter:</span>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button type="button" onClick={() => setTargetTagMatch('any')} className={`rounded-full border px-3 py-1.5 text-xs ${targetTagMatch === 'any' ? 'border-blue-500 bg-blue-600 text-white' : 'border-gray-300 bg-white'}`}>Qualquer uma</button>
                            <button type="button" onClick={() => setTargetTagMatch('all')} className={`rounded-full border px-3 py-1.5 text-xs ${targetTagMatch === 'all' ? 'border-blue-500 bg-blue-600 text-white' : 'border-gray-300 bg-white'}`}>Todas</button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Categorias de Contatos (agrupadas por funil) */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Categorias de Contatos
                        </label>
                        {contactTags.length > 0 && (
                          <button
                            type="button"
                            onClick={handleSelectAllTags}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                          >
                            {formData.targetTags.length === contactTags.length ? 'Desmarcar todas' : 'Selecionar todas'}
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3">Selecione uma ou mais categorias. Categorias múltiplas sempre ampliam o público.</p>
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        placeholder="🔍 Buscar categoria..."
                        className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {groupCategoriesByFunnel(filteredContactTags).map((group) => (
                          <div key={group.funnelId || 'sem-funil'} className="mb-2">
                            <div className="flex items-center gap-2 px-1 py-1 select-none">
                              {group.funnelCor && (
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: group.funnelCor }}></span>
                              )}
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{group.funnelNome}</span>
                            </div>
                            {group.categories.map((tag) => (
                              <label key={tag.id} className="flex items-center space-x-2 mb-1 ml-4 p-2 rounded hover:bg-white cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.targetTags.includes(tag.id)}
                                  onChange={() => handleTagToggle(tag.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 font-medium">{tag.nome}</span>
                                {audiencePreview && (
                                  <span className="ml-auto text-xs text-gray-500">{(audiencePreview?.availableCounts?.categories[tag.id] ?? audiencePreview?.counts.categories[tag.id] ?? 0).toLocaleString('pt-BR')} contatos</span>
                                )}
                              </label>
                            ))}
                          </div>
                        ))}
                        {contactTags.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria disponível</p>
                        )}
                        {contactTags.length > 0 && filteredContactTags.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria encontrada para "{categorySearch}"</p>
                        )}
                      </div>
                      {formData.targetTags.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.targetTags.length} categoria(s) selecionada(s)</p>
                      )}
                    </div>

                    {/* Etiquetas de Contatos */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Etiquetas de Contatos
                        </label>
                        {formData.targetContactTags.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, targetContactTags: [] }))}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                          >
                            Limpar seleção
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3">Selecione uma ou mais etiquetas e use os controles acima para definir como elas serão combinadas.</p>
                      <input
                        type="text"
                        value={etiquetaSearch}
                        onChange={(e) => setEtiquetaSearch(e.target.value)}
                        placeholder="🔍 Buscar etiqueta..."
                        className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {filteredEtiquetas.map((etiqueta) => (
                          <label key={etiqueta} className="flex items-center space-x-2 mb-1 p-2 rounded hover:bg-white cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.targetContactTags.includes(etiqueta)}
                              onChange={() => handleEtiquetaToggle(etiqueta)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: contactEtiquetaColors[etiqueta.trim().toLowerCase()] || '#64748b' }} />
                            <span className="rounded-full border px-2.5 py-1 text-sm font-medium text-white" style={{ backgroundColor: contactEtiquetaColors[etiqueta.trim().toLowerCase()] || '#64748b', borderColor: contactEtiquetaColors[etiqueta.trim().toLowerCase()] || '#64748b' }}>{etiqueta}</span>
                            {audiencePreview && (
                              <span className="ml-auto text-xs text-gray-500">{(audiencePreview?.availableCounts?.tags[etiqueta] ?? audiencePreview?.counts.tags[etiqueta] ?? 0).toLocaleString('pt-BR')} contatos</span>
                            )}
                          </label>
                        ))}
                        {contactEtiquetas.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma etiqueta encontrada nos contatos</p>
                        )}
                        {contactEtiquetas.length > 0 && filteredEtiquetas.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma etiqueta encontrada para "{etiquetaSearch}"</p>
                        )}
                      </div>
                      {formData.targetContactTags.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.targetContactTags.length} etiqueta(s) selecionada(s)</p>
                      )}
                    </div>
                    <div className="sticky bottom-0 rounded-xl border border-gray-200 bg-white/95 p-4 shadow-lg backdrop-blur">
                      <p className="text-sm text-gray-700">{audienceSummary}</p>
                      <div className="mt-2 flex items-end justify-between">
                        <div>
                          <p className="text-xs text-gray-500">As contagens individuais podem se sobrepor. O total abaixo remove duplicados.</p>
                          <p className="mt-1 text-2xl font-bold text-blue-700">{audienceLoading ? 'Calculando…' : `${audienceTotal.toLocaleString('pt-BR')} ${audienceTotalLabel}`}</p>
                          {!!((audiencePreview?.duplicatePhones || 0) + (audiencePreview?.invalidPhones || 0)) && (
                            <p className="mt-1 text-xs text-amber-700">
                              {(audiencePreview?.duplicatePhones || 0).toLocaleString('pt-BR')} telefones duplicados e {(audiencePreview?.invalidPhones || 0).toLocaleString('pt-BR')} inválidos serão descartados.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    </>)}

                    {/* Tipo de Envio: Não-oficial vs API Oficial */}
                    {wizardStep === 2 && (<>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <span className="text-xs font-medium text-gray-600">Tipo de envio:</span>
                      <button
                        type="button"
                        onClick={() => { setDeliveryProvider('unofficial'); setUseOfficialApi(false); setFormLateAccountId(''); setFormLateTemplateName(''); setFormTemplateText(''); setLateTemplates([]); }}
                        className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${deliveryProvider === 'unofficial' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      >
                        📱 WhatsApp Não-oficial
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeliveryProvider('official'); setUseOfficialApi(true); setFormData(prev => ({ ...prev, sessionNames: [] })); }}
                        className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${deliveryProvider === 'official' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      >
                        ✅ API Oficial WhatsApp
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeliveryProvider('allcance'); setUseOfficialApi(false); setFormData(prev => ({ ...prev, sessionNames: [] })); }}
                        className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${deliveryProvider === 'allcance' ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      >
                        🛡️ Número de terceiros
                      </button>
                    </div>

                    {deliveryProvider === 'allcance' && (
                      <div className="space-y-4 rounded-xl border border-violet-200 bg-violet-50 p-4">
                        <div>
                          <h4 className="font-semibold text-violet-900">Envio por número de terceiros</h4>
                          <p className="mt-1 text-sm text-violet-800">
                            Este é o tipo de envio mais seguro contra banimento, pois usa números de terceiros.
                            As respostas não chegam ao seu WhatsApp: escreva uma mensagem sólida e direcione o contato
                            para um número ou link da sua empresa.
                          </p>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {[
                            { withButton: false, title: 'Sem botão', price: allcanceStandardPrice, minimum: 1000 },
                            { withButton: true, title: 'Com 1 botão de link', price: allcanceButtonPrice, minimum: 5000 },
                          ].map(option => (
                            <button
                              key={option.title}
                              type="button"
                              onClick={() => setAllcanceForm(prev => ({ ...prev, withButton: option.withButton }))}
                              className={`rounded-lg border p-3 text-left ${allcanceForm.withButton === option.withButton ? 'border-violet-500 bg-white ring-2 ring-violet-100' : 'border-violet-200 bg-violet-50'}`}
                            >
                              <span className="block text-sm font-semibold text-gray-900">{option.title}</span>
                              <span className="mt-1 block text-xs text-gray-600">{option.price.toFixed(2).replace('.', ',')} crédito/contato · mínimo {option.minimum.toLocaleString('pt-BR')}</span>
                            </button>
                          ))}
                        </div>
                        <div className={`rounded-lg border px-3 py-2 text-sm ${audienceTotal >= allcanceMinimum ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                          Público: <strong>{audienceLoading ? 'calculando…' : audienceTotal.toLocaleString('pt-BR')}</strong> {audienceTotalLabel} ·
                          custo estimado <strong>{allcanceEstimatedCost.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} créditos</strong>.
                          {audienceTotal < allcanceMinimum && <> Faltam {(allcanceMinimum - audienceTotal).toLocaleString('pt-BR')} contatos para o mínimo.</>}
                        </div>
                        <div className="grid gap-5 lg:grid-cols-2">
                          <div className="space-y-3">
                            <div>
                              <label className="mb-1 block text-sm font-medium text-gray-700">Nome exibido no perfil *</label>
                              <input
                                type="text"
                                value={allcanceForm.profileName}
                                onChange={event => setAllcanceForm(prev => ({ ...prev, profileName: event.target.value }))}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100"
                                placeholder="Nome da sua empresa"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-sm font-medium text-gray-700">Foto do perfil (opcional)</label>
                              <div className="flex items-center gap-3">
                                {allcanceForm.profilePhotoBase64 && <img src={allcanceForm.profilePhotoBase64} alt="" className="h-12 w-12 rounded-full object-cover" />}
                                <input type="file" accept="image/*" onChange={event => handleAllcanceProfilePhoto(event.target.files?.[0])} className="block w-full text-xs text-gray-600" />
                              </div>
                            </div>
                            <div>
                              <label className="mb-1 flex justify-between text-sm font-medium text-gray-700">
                                <span>Mensagem *</span>
                                {allcanceForm.withButton && <span className={allcanceForm.text.length > 500 ? 'text-red-600' : 'text-gray-500'}>{allcanceForm.text.length}/500</span>}
                              </label>
                              <textarea
                                rows={7}
                                maxLength={allcanceForm.withButton ? 500 : undefined}
                                value={allcanceForm.text}
                                onChange={event => setAllcanceForm(prev => ({ ...prev, text: event.target.value }))}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100"
                                placeholder={'Crie uma mensagem clara. Use *negrito*, _itálico_ e ~tachado~. Inclua seu WhatsApp ou site para receber respostas.'}
                              />
                            </div>
                            {allcanceForm.withButton && (
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-gray-700">Texto do botão *</label>
                                  <input value={allcanceForm.buttonText} onChange={event => setAllcanceForm(prev => ({ ...prev, buttonText: event.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2" placeholder="Falar no WhatsApp" />
                                </div>
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-gray-700">Link do botão *</label>
                                  <input type="url" value={allcanceForm.buttonUrl} onChange={event => setAllcanceForm(prev => ({ ...prev, buttonUrl: event.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2" placeholder="https://wa.me/5511..." />
                                </div>
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="mb-2 text-sm font-semibold text-gray-700">Como a mensagem será vista</p>
                            <CampaignMessagePreview
                              messages={[{ type: 'text', content: { text: allcanceForm.text } }]}
                              profileName={allcanceForm.profileName || 'Sua empresa'}
                              profilePhoto={allcanceForm.profilePhotoBase64}
                              buttonText={allcanceForm.withButton ? allcanceForm.buttonText || 'Abrir link' : undefined}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* API Oficial: Conta + Template */}
                    {useOfficialApi && (
                      <div className="space-y-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-medium text-green-800">API Oficial WhatsApp</h4>
                            <p className="text-sm text-green-700 mt-1">
                              Sistema oficial do WhatsApp para envio de mensagens em massa.
                            </p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Conta WhatsApp Oficial</label>
                          <select
                            value={formLateAccountId}
                            onChange={(e) => { setFormLateAccountId(e.target.value); setFormLateTemplateName(''); setFormTemplateText(''); loadLateTemplates(e.target.value); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">Selecione a conta...</option>
                            {lateAccounts.map((acc) => (
                              <option key={acc.id} value={acc.platformAccountId || acc.id}>{acc.accountName}{acc.accountUsername ? ` (@${acc.accountUsername})` : ''}{acc.groupName ? ` - ${acc.groupName}` : ''}</option>
                            ))}
                          </select>
                          {lateAccounts.length === 0 && <p className="text-xs text-yellow-600 mt-1">⚠️ Nenhuma conta encontrada. Configure em WhatsApp Oficial.</p>}
                        </div>
                        {formLateAccountId && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
                            {loadingTemplates ? (
                              <div className="text-xs text-gray-500 py-2">Carregando templates...</div>
                            ) : (
                              <select
                                value={formLateTemplateName}
                                onChange={(e) => {
                                  const selected = lateTemplates.find(t => t.name === e.target.value);
                                  if (selected && !isTemplateCompatibleWithCampaign(selected)) {
                                    const invalidKeys = [
                                      ...getTemplateIncompatibleVariables(selected),
                                      ...getTemplateUnknownVariables(selected)
                                    ];
                                    toast.error(`Este template não pode ser usado em campanhas de contato: ${invalidKeys.join(', ')}`);
                                    setFormLateTemplateName('');
                                    setFormLateTemplateLang('pt_BR');
                                    setFormTemplateText('');
                                    setFormTemplateVarOrder([]);
                                    return;
                                  }
                                  setFormLateTemplateName(e.target.value);
                                  if (selected) setFormLateTemplateLang(selected.language || 'pt_BR');
                                  const bodyText = selected ? getTemplateBodyText(selected) : '';
                                  if (bodyText) setFormTemplateText(bodyText);
                                  else setFormTemplateText('');
                                  // Ordem COMPLETA (com textos fixos e link de botão):
                                  // getTemplateVariables filtra os literais e serve só
                                  // para validar compatibilidade.
                                  setFormTemplateVarOrder(selected ? getTemplateOrder(selected) : []);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                              >
                                <option value="">Selecione o template...</option>
                                {lateTemplates.map((t) => (
                                  <option key={t.id || t.name} value={t.name} disabled={!isTemplateCompatibleWithCampaign(t)}>{t.name} ({t.language}) {t.status !== 'APPROVED' ? `— ${t.status}` : '✓'} {!isTemplateCompatibleWithCampaign(t) ? '— incompatível com campanha' : ''}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}
                        {formLateTemplateName && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Corpo do Template</label>
                            <textarea
                              value={formTemplateText}
                              rows={4}
                              className="w-full px-3 py-2 border border-green-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                              readOnly
                            />

                            {selectedTemplateIncompatibleVariables.length > 0 && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-xs font-medium text-red-700">Template incompatível com campanhas de contato</p>
                                <p className="text-xs text-red-600 mt-1">Variáveis de pedido/e-commerce encontradas: {selectedTemplateIncompatibleVariables.map(key => CAMPAIGN_VARIABLE_LABELS[key] || key).join(', ')}</p>
                              </div>
                            )}
                            {selectedTemplateUnknownVariables.length > 0 && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-xs font-medium text-yellow-700">Template com variáveis sem vínculo com contato</p>
                                <p className="text-xs text-yellow-600 mt-1">Variáveis encontradas: {selectedTemplateUnknownVariables.map(key => CAMPAIGN_VARIABLE_LABELS[key] || key).join(', ')}</p>
                              </div>
                            )}
                            {formTemplateVarOrder.length > 0 && (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-xs font-medium text-blue-700 mb-1">Mapeamento de variáveis (ordem de envio):</p>
                                <div className="flex flex-col gap-0.5">
                                  {/* key pelo índice: dois textos fixos iguais gerariam
                                      keys duplicadas se usássemos o valor. */}
                                  {formTemplateVarOrder.map((entry, i) => (
                                    <span key={i} className="text-xs text-blue-600">
                                      <span className="font-mono bg-blue-100 px-1 rounded">{`{{${i + 1}}}`}</span>
                                      {' → '}
                                      <span className="font-medium">{mappingEntryLabel(entry)}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <p className="text-xs text-gray-400 mt-1">Quando existir, o mapeamento central salvo do template é o usado na campanha.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Conexões WhatsApp (apenas não-oficial) */}
                    {deliveryProvider === 'unofficial' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Conexões WhatsApp * ({formData.sessionNames.length} selecionadas)
                        </label>
                        {whatsappSessions.length > 0 && (
                          <button
                            type="button"
                            onClick={handleSelectAllSessions}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                          >
                            {formData.sessionNames.length === whatsappSessions.length ? 'Desmarcar todas' : 'Selecionar todas'}
                          </button>
                        )}
                      </div>
                      <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-medium text-green-800">Multi-Sessão com Failover</h4>
                            <p className="text-sm text-green-700 mt-1">
                              Sistema inteligente que distribui envios entre múltiplas conexões com redundância automática.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {whatsappSessions.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma conexão WhatsApp disponível</p>
                        ) : (
                          whatsappSessions.map((session) => (
                            <label key={session.name} className="flex items-center justify-between space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.sessionNames.includes(session.name)}
                                  onChange={() => handleSessionToggle(session.name)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                  <span className="text-sm font-medium text-gray-700">
                                    {session.mePushName || session.displayName || session.name}
                                  </span>
                                  <span className="text-xs text-gray-500 block">({session.displayName || session.name})</span>
                                </div>
                              </div>
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                Ativa
                              </span>
                              <WahaHealthAlert health={session.health} compact />
                            </label>
                          ))
                        )}
                      </div>
                      {formData.sessionNames.length > 0 && (
                        <>
                          <p className="text-xs text-green-600 mt-2">✅ {formData.sessionNames.length} conexão(ões) selecionada(s)</p>
                          {whatsappSessions
                            .filter((session) => formData.sessionNames.includes(session.name) && session.health?.hasIssue)
                            .map((session) => (
                              <WahaHealthAlert key={session.name} health={session.health} className="mt-2" />
                            ))}
                        </>
                      )}
                    </div>
                    )}
                    </>)}

                    {/* ETAPA 3 — Etiquetas e funil: o que muda no contato após o disparo */}
                    {wizardStep === 3 && (<>
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <h4 className="text-lg font-semibold text-amber-900 mb-1">Etiquetas e funil</h4>
                      <p className="text-sm text-amber-800">
                        Aplicado automaticamente a <strong>cada contato</strong> assim que a mensagem dele é enviada com sucesso.
                        Deixe em branco para não alterar nada.
                      </p>
                    </div>

                    {/* Remover etiquetas */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Remover etiqueta atual</label>
                      <label className="mb-2 flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={postSendActions.removeAllTags}
                          onChange={(e) => setPostSendActions(prev => ({ ...prev, removeAllTags: e.target.checked, removeTags: e.target.checked ? [] : prev.removeTags }))}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                        Remover <strong>todas</strong> as etiquetas do contato
                      </label>
                      {!postSendActions.removeAllTags && (
                        <>
                          <input
                            type="text"
                            value={postSendRemoveSearch}
                            onChange={(e) => setPostSendRemoveSearch(e.target.value)}
                            placeholder="🔍 Buscar etiqueta..."
                            className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                          <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                            {postSendRemoveOptions.map(etiqueta => (
                              <label key={etiqueta} className="flex items-center space-x-2 mb-1 p-2 rounded hover:bg-white cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={postSendActions.removeTags.includes(etiqueta)}
                                  onChange={(e) => setPostSendActions(prev => ({
                                    ...prev,
                                    removeTags: e.target.checked
                                      ? [...prev.removeTags, etiqueta]
                                      : prev.removeTags.filter(t => t !== etiqueta)
                                  }))}
                                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: etiquetaColor(etiqueta) }} />
                                <EtiquetaChip etiqueta={etiqueta} />
                              </label>
                            ))}
                            {contactEtiquetas.length === 0 && (
                              <p className="text-sm text-gray-500 text-center py-4">Nenhuma etiqueta encontrada nos contatos</p>
                            )}
                            {contactEtiquetas.length > 0 && postSendRemoveOptions.length === 0 && (
                              <p className="text-sm text-gray-500 text-center py-4">Nenhuma etiqueta encontrada para "{postSendRemoveSearch}"</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Adicionar etiquetas */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Etiquetar o contato</label>
                      <div className="mb-2 flex gap-2">
                        <input
                          type="text"
                          value={newPostSendTag}
                          onChange={(e) => setNewPostSendTag(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            const clean = newPostSendTag.trim();
                            if (!clean) return;
                            setPostSendActions(prev => prev.addTags.includes(clean) ? prev : { ...prev, addTags: [...prev.addTags, clean] });
                            setNewPostSendTag('');
                          }}
                          placeholder="Nova etiqueta e Enter, ou escolha abaixo"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      {postSendActions.addTags.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1.5">
                          {postSendActions.addTags.map(tag => (
                            <button
                              key={tag}
                              type="button"
                              title="Remover desta lista"
                              onClick={() => setPostSendActions(prev => ({ ...prev, addTags: prev.addTags.filter(t => t !== tag) }))}
                              className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-medium text-white hover:opacity-85"
                              style={{ backgroundColor: etiquetaColor(tag), borderColor: etiquetaColor(tag) }}
                            >
                              {tag} <span className="text-white/80">×</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <input
                        type="text"
                        value={postSendAddSearch}
                        onChange={(e) => setPostSendAddSearch(e.target.value)}
                        placeholder="🔍 Buscar etiqueta existente..."
                        className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {postSendAddOptions.map(etiqueta => {
                          const selected = postSendActions.addTags.includes(etiqueta);
                          return (
                            <label key={etiqueta} className="flex items-center space-x-2 mb-1 p-2 rounded hover:bg-white cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => setPostSendActions(prev => ({
                                  ...prev,
                                  addTags: e.target.checked
                                    ? [...prev.addTags, etiqueta]
                                    : prev.addTags.filter(t => t !== etiqueta)
                                }))}
                                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                              />
                              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: etiquetaColor(etiqueta) }} />
                              <EtiquetaChip etiqueta={etiqueta} />
                            </label>
                          );
                        })}
                        {contactEtiquetas.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma etiqueta encontrada nos contatos</p>
                        )}
                        {contactEtiquetas.length > 0 && postSendAddOptions.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma etiqueta encontrada para "{postSendAddSearch}"</p>
                        )}
                      </div>
                    </div>

                    {/* Mover no funil */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Mover para categoria de funil</label>
                        {postSendActions.moveToCategoriaId && (
                          <button
                            type="button"
                            onClick={() => setPostSendActions(prev => ({ ...prev, moveToCategoriaId: null }))}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                          >
                            Limpar seleção
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        Escolha UMA categoria. O contato passa a pertencer a ela — é o mesmo campo usado pelos funis.
                      </p>
                      <input
                        type="text"
                        value={postSendCategorySearch}
                        onChange={(e) => setPostSendCategorySearch(e.target.value)}
                        placeholder="🔍 Buscar categoria ou funil..."
                        className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                      <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {/* Seleção única: radio. Funil é cabeçalho, categorias indentadas. */}
                        <label className="flex items-center space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                          <input
                            type="radio"
                            name="postSendCategoria"
                            checked={!postSendActions.moveToCategoriaId}
                            onChange={() => setPostSendActions(prev => ({ ...prev, moveToCategoriaId: null }))}
                            className="border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm text-gray-600">Não mover o contato</span>
                        </label>
                        {groupCategoriesByFunnel(postSendCategoryOptions).map((group) => (
                          <div key={group.funnelId || 'sem-funil'} className="mb-2">
                            <div className="flex items-center gap-2 px-1 py-1 select-none">
                              {group.funnelCor && (
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: group.funnelCor }}></span>
                              )}
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{group.funnelNome}</span>
                            </div>
                            {group.categories.map((tag) => (
                              <label key={tag.id} className="flex items-center space-x-2 mb-1 ml-4 p-2 rounded hover:bg-white cursor-pointer">
                                <input
                                  type="radio"
                                  name="postSendCategoria"
                                  checked={postSendActions.moveToCategoriaId === tag.id}
                                  onChange={() => setPostSendActions(prev => ({ ...prev, moveToCategoriaId: tag.id }))}
                                  className="border-gray-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="text-sm text-gray-700 font-medium">{tag.nome}</span>
                              </label>
                            ))}
                          </div>
                        ))}
                        {contactTags.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria disponível</p>
                        )}
                        {contactTags.length > 0 && postSendCategoryOptions.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria encontrada para "{postSendCategorySearch}"</p>
                        )}
                      </div>
                    </div>


                    {(postSendActions.removeAllTags || postSendActions.removeTags.length > 0 || postSendActions.addTags.length > 0 || postSendActions.moveToCategoriaId) && (
                      <div className="rounded-lg border border-amber-200 bg-white p-3 text-xs text-amber-800">
                        <p className="font-semibold mb-1">Resumo do que será aplicado por contato:</p>
                        <ul className="space-y-0.5">
                          {postSendActions.removeAllTags && <li>• Remover todas as etiquetas</li>}
                          {!postSendActions.removeAllTags && postSendActions.removeTags.length > 0 && <li>• Remover: {postSendActions.removeTags.join(', ')}</li>}
                          {postSendActions.addTags.length > 0 && <li>• Adicionar: {postSendActions.addTags.join(', ')}</li>}
                          {postSendActions.moveToCategoriaId && (
                            <li>• Mover para: {(() => {
                              const cat = contactTags.find(c => c.id === postSendActions.moveToCategoriaId);
                              return cat ? (cat.funnelNome ? `${cat.funnelNome} · ${cat.nome}` : cat.nome) : '—';
                            })()}</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Atribuição em modo leitura: quem atende é escolhido na
                        etapa 2, junto com a mensagem de cada um. */}
                    {postSendActions.assignToUserIds.length > 0 && (
                      <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900">
                        <p>
                          {postSendActions.assignToUserIds.length > 1 ? 'Conversas em rodízio: ' : 'Conversas atribuídas a: '}
                          <strong>
                            {postSendAssignedUsers.length > 0
                              ? postSendAssignedUsers.map(user => user.nome || user.email).join(' → ')
                              : '—'}
                          </strong>
                          {' '}(definido na etapa 2, junto com a mensagem).
                        </p>
                      </div>
                    )}
                    </>)}

                    {wizardStep === 4 && deliveryProvider !== 'allcance' && (<>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Intervalo de Randomização (segundos)
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Mínimo</label>
                          <input
                            type="number"
                            min="0"
                            max="600"
                            value={formData.randomDelay}
                            onChange={(e) => {
                              const nextMin = Math.min(parseInt(e.target.value) || 0, 600)
                              setFormData(prev => ({
                                ...prev,
                                randomDelay: nextMin,
                                randomDelayMax: Math.max(prev.randomDelayMax ?? prev.randomDelay, nextMin)
                              }))
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <span className="text-gray-400 font-medium mt-5">a</span>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Máximo</label>
                          <input
                            type="number"
                            min={formData.randomDelay}
                            max="600"
                            value={formData.randomDelayMax ?? formData.randomDelay}
                            onChange={(e) => {
                              const parsedValue = parseInt(e.target.value) || 0
                              const nextMax = Math.min(Math.max(parsedValue, formData.randomDelay), 600)
                              setFormData(prev => ({ ...prev, randomDelayMax: nextMax }))
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        ⏱️ Intervalo aleatório entre envios ({formData.randomDelay}s - {formData.randomDelayMax ?? formData.randomDelay}s) para evitar bloqueios
                      </p>
                    </div>

                    {/* Data de Envio */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quando Enviar
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: true }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">🚀 Iniciar imediatamente</span>
                            <p className="text-xs text-gray-500">A campanha será executada assim que for criada</p>
                          </div>
                        </label>
                        <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            checked={!formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: false }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">📅 Agendar para:</span>
                            <p className="text-xs text-gray-500">Escolha data e hora específicas</p>
                          </div>
                        </label>
                        {!formData.startImmediately && (
                          <input
                            type="datetime-local"
                            value={formData.scheduledFor}
                            onChange={(e) => setFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ml-6"
                            required={!formData.startImmediately}
                          />
                        )}
                      </div>
                    </div>

                    {/* Horários e Dias de Disparo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        📅 Horários e Dias de Disparo (Opcional)
                      </label>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-3">
                        <p className="text-sm text-yellow-800">
                          ⏰ Configure os dias da semana e horários permitidos para envio das mensagens.
                          A campanha será pausada automaticamente fora desses períodos.
                        </p>
                      </div>

                      <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                        {/* Dias da Semana */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Dias da Semana
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Dia de Início</label>
                              <select
                                value={formData.scheduleStartDay ?? ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  scheduleStartDay: e.target.value ? parseInt(e.target.value) : null
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Sem restrição</option>
                                <option value="0">Domingo</option>
                                <option value="1">Segunda-feira</option>
                                <option value="2">Terça-feira</option>
                                <option value="3">Quarta-feira</option>
                                <option value="4">Quinta-feira</option>
                                <option value="5">Sexta-feira</option>
                                <option value="6">Sábado</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Dia de Fim</label>
                              <select
                                value={formData.scheduleEndDay ?? ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  scheduleEndDay: e.target.value ? parseInt(e.target.value) : null
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Sem restrição</option>
                                <option value="0">Domingo</option>
                                <option value="1">Segunda-feira</option>
                                <option value="2">Terça-feira</option>
                                <option value="3">Quarta-feira</option>
                                <option value="4">Quinta-feira</option>
                                <option value="5">Sexta-feira</option>
                                <option value="6">Sábado</option>
                              </select>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Exemplo: Segunda a Sexta-feira
                          </p>
                        </div>

                        {/* Horários */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Horários
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Hora de Início</label>
                              <select
                                value={formData.scheduleStartHour ?? ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  scheduleStartHour: e.target.value ? parseInt(e.target.value) : null
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Sem restrição</option>
                                {Array.from({ length: 24 }, (_, i) => (
                                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Hora de Fim</label>
                              <select
                                value={formData.scheduleEndHour ?? ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  scheduleEndHour: e.target.value ? parseInt(e.target.value) : null
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Sem restrição</option>
                                {Array.from({ length: 24 }, (_, i) => (
                                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Exemplo: Das 8h às 18h
                          </p>
                        </div>
                      </div>
                    </div>
                    </>)}

                    {wizardStep === 4 && deliveryProvider === 'allcance' && (
                      <div className="space-y-5">
                        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                          <h4 className="font-semibold text-violet-900">Envio em lote por número de terceiros</h4>
                          <p className="mt-1 text-sm text-violet-800">
                            A lista inteira é entregue ao provedor de uma vez. Por isso, este canal não possui intervalo,
                            randomização, dias da semana ou janela de horários.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Quando enviar</label>
                          <div className="space-y-3">
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-gray-50">
                              <input type="radio" checked={formData.startImmediately} onChange={() => setFormData(prev => ({ ...prev, startImmediately: true, scheduledFor: '' }))} />
                              <div><p className="text-sm font-medium">Iniciar imediatamente</p><p className="text-xs text-gray-500">A campanha será enviada ao provedor após a criação.</p></div>
                            </label>
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-gray-50">
                              <input type="radio" checked={!formData.startImmediately} onChange={() => setFormData(prev => ({ ...prev, startImmediately: false }))} />
                              <div><p className="text-sm font-medium">Agendar disparo</p><p className="text-xs text-gray-500">Escolha a data e hora para entregar o lote.</p></div>
                            </label>
                            {!formData.startImmediately && (
                              <input type="datetime-local" required value={formData.scheduledFor} onChange={event => setFormData(prev => ({ ...prev, scheduledFor: event.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2" />
                            )}
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                          <p className="text-sm text-gray-600">Resumo</p>
                          <p className="mt-1 font-semibold text-gray-900">{audienceTotal.toLocaleString('pt-BR')} contatos · {allcanceForm.withButton ? 'com botão' : 'sem botão'}</p>
                          <p className="mt-1 text-sm text-gray-700">Custo estimado: {allcanceEstimatedCost.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} créditos</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* COLUNA DIREITA - Mensagens */}
                  {wizardStep === 2 && deliveryProvider !== 'allcance' && (<div className="space-y-6">
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h4 className="text-lg font-semibold text-purple-900 mb-2">💬 Mensagens da Campanha</h4>
                      <p className="text-sm text-purple-700">{useOfficialApi ? 'Na API Oficial, o conteúdo vem do template aprovado na Meta.' : 'Configure o conteúdo que será enviado aos contatos'}</p>
                    </div>
                    {/* Rodízio de atendimento: fica aqui, na etapa da mensagem,
                        porque cada usuário pode ter a SUA mensagem — é preciso
                        escolher quem atende antes de escrever o que ele manda. */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Quem vai atender estas conversas</label>
                        {postSendActions.assignToUserIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setPostSendActions(prev => ({ ...prev, assignToUserIds: [] }))}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                          >
                            Limpar seleção
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        Opcional. A conversa criada no Chat CRM por este disparo fica atribuída ao usuário — ele vê a tag
                        piscando com o nome dele na lista de conversas. <strong>Selecionando mais de um</strong>, as conversas
                        são distribuídas em rodízio, na ordem em que você marcar: o 1º disparo vai para o 1º, o 2º para o 2º,
                        e assim por diante, voltando ao começo. Cada um pode ainda ter a <strong>sua própria mensagem</strong>
                        {' '}(abas logo abaixo). Se a conversa já estiver atribuída a outra pessoa, esta escolha substitui a
                        atribuição anterior.
                      </p>
                      <input
                        type="text"
                        value={postSendUserSearch}
                        onChange={(e) => setPostSendUserSearch(e.target.value)}
                        placeholder="🔍 Buscar usuário por nome ou e-mail..."
                        className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                      <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {postSendUserOptions.map((user) => {
                          const position = postSendActions.assignToUserIds.indexOf(user.id);
                          return (
                            <label key={user.id} className="flex items-center space-x-2 mb-1 p-2 rounded hover:bg-white cursor-pointer">
                              <input
                                type="checkbox"
                                checked={position >= 0}
                                onChange={() => togglePostSendUser(user.id)}
                                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                              />
                              {/* Posição no rodízio: deixa a ordem explícita, já que ela define quem recebe cada disparo. */}
                              {position >= 0 && (
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                                  {position + 1}
                                </span>
                              )}
                              <span className="text-sm text-gray-700 font-medium">{user.nome || user.email}</span>
                              {user.nome && <span className="text-xs text-gray-500">{user.email}</span>}
                            </label>
                          );
                        })}
                        {tenantUsers.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhum usuário disponível</p>
                        )}
                        {tenantUsers.length > 0 && postSendUserOptions.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhum usuário encontrado para "{postSendUserSearch}"</p>
                        )}
                      </div>
                      {tenantUsers.length === 1 && !postSendUserSearch.trim() && (
                        <p className="mt-2 text-xs text-gray-500">
                          Só aparece você nesta lista: contas com perfil de operador enxergam apenas o próprio usuário.
                          Peça a um administrador para montar a campanha se precisar atribuir a um colega.
                        </p>
                      )}
                      {postSendActions.assignToUserIds.length > 1 && (
                        <p className="mt-2 text-xs text-gray-600">
                          Rodízio entre {postSendActions.assignToUserIds.length} usuários
                          {(() => {
                            const total = audiencePreview?.totalRecipients ?? audiencePreview?.totalContacts ?? 0;
                            if (!total) return null;
                            const base = Math.floor(total / postSendActions.assignToUserIds.length);
                            const resto = total % postSendActions.assignToUserIds.length;
                            return ` — cerca de ${base}${resto ? ` a ${base + 1}` : ''} conversas para cada um dos ${total} destinatários`;
                          })()}
                          .
                        </p>
                      )}
                      {/* Allcance não chega aqui: a coluna inteira é renderizada
                          só quando deliveryProvider !== 'allcance'. E é coerente,
                          porque disparo Allcance não gera conversa no Chat CRM. */}
                    </div>

                    {/* Abas de mensagem: a padrão + uma por usuário do rodízio.
                        O editor abaixo é um só; a aba escolhe para qual rascunho
                        ele aponta (activeMessageDraft). */}
                    {postSendActions.assignToUserIds.length > 0 && (
                      <div>
                        <p className="mb-2 text-sm font-medium text-gray-700">Mensagem</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setActiveMessageDraft('default')}
                            className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${activeMessageDraft === 'default' ? 'border-purple-400 bg-purple-100 text-purple-800' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
                          >
                            Padrão
                          </button>
                          {postSendAssignedUsers.map((user, index) => {
                            const own = useOfficialApi
                              ? Boolean(perUserTemplates[user.id]?.lateTemplateName)
                              : (perUserMessages[user.id]?.sequence?.length || 0) > 0;
                            return (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => setActiveMessageDraft(user.id)}
                                title={own ? 'Tem mensagem própria' : 'Usando a mensagem padrão'}
                                className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${activeMessageDraft === user.id ? 'border-purple-400 bg-purple-100 text-purple-800' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
                              >
                                {index + 1}. {user.nome || user.email}{own ? ' *' : ''}
                              </button>
                            );
                          })}
                        </div>
                        {activeMessageDraft !== 'default' && (
                          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900">
                            <span>
                              Editando a mensagem de <strong>{postSendAssignedUsers.find(u => u.id === activeMessageDraft)?.nome || activeMessageDraft}</strong>.
                              {' '}Deixando vazia, esse usuário dispara a mensagem padrão.
                            </span>
                            {!useOfficialApi && activeSequence.length === 0 && defaultSequence.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setActiveSequence(JSON.parse(JSON.stringify(defaultSequence)))}
                                className="rounded border border-purple-300 bg-white px-2 py-1 font-semibold text-purple-800 hover:bg-purple-100"
                              >
                                Copiar da padrão para editar
                              </button>
                            )}
                            {activeSequence.length > 0 && !useOfficialApi && (
                              <button
                                type="button"
                                onClick={() => setActiveSequence([])}
                                className="rounded border border-purple-300 bg-white px-2 py-1 font-semibold text-purple-800 hover:bg-purple-100"
                              >
                                Voltar a usar a padrão
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <p className="mb-2 text-sm font-semibold text-gray-700">Prévia no WhatsApp</p>
                      <CampaignMessagePreview
                        official={useOfficialApi}
                        profileName={
                          useOfficialApi
                            ? lateAccounts.find(account => (account.platformAccountId || account.id) === formLateAccountId)?.accountName || 'Conta oficial'
                            : whatsappSessions.find(session => formData.sessionNames.includes(session.name))?.mePushName || 'Sua empresa'
                        }
                        messages={
                          useOfficialApi
                            ? [{ type: 'text', content: { text: formTemplateText } }]
                            : (activeSequence)
                        }
                      />
                      <p className="mt-2 text-xs text-gray-500">A prévia usa dados de exemplo; variáveis serão personalizadas para cada contato.</p>
                    </div>

                    {!useOfficialApi ? (
                    <div>
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-medium text-blue-800">💡 Variáveis Dinâmicas</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Personalize suas mensagens com:
                              <span className="block mt-1">
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{nome}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{primeironome}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{telefone}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{email}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{categoria}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{observacoes}}'}</code>
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-600">Adicione uma ou mais mensagens para serem enviadas em sequência</p>
                          <button
                            type="button"
                            onClick={() => {
                              const currentSequence = activeSequence;
                              const newSequence = [...currentSequence, { type: 'text', content: { text: '' } }];
                              setActiveSequence(newSequence);
                            }}
                            className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-2"
                          >
                            <span className="text-lg">+</span>
                            Mensagem
                          </button>
                        </div>

                        <div className="space-y-3">
                          {activeSequence.map((item, index) => (
                            <div
                              key={index}
                              draggable
                              onDragStart={() => handleDragStart(index)}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnd={handleDragEnd}
                              className={`border rounded-lg p-4 transition-all cursor-move ${
                                draggedIndex === index
                                  ? 'border-blue-500 bg-blue-50 opacity-50'
                                  : draggedIndex !== null
                                  ? 'border-gray-300 bg-gray-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400 cursor-move text-xl">⋮⋮</span>
                                  <span className="text-sm font-medium text-gray-600">Mensagem {index + 1}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentSequence = activeSequence;
                                    const newSequence = currentSequence.filter((_, i) => i !== index);
                                    setActiveSequence(newSequence);
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Remover
                                </button>
                              </div>

                              <div className="space-y-3">
                                <select
                                  value={item.type}
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    let newContent;
                                    switch (newType) {
                                      case 'text':
                                        newContent = { text: '' };
                                        break;
                                      case 'document':
                                        newContent = { url: '', fileName: '' };
                                        break;
                                      case 'openai':
                                        newContent = { model: 'cx/gpt-6-luna', system: '', user: '' };
                                        break;
                                      case 'groq':
                                        newContent = { model: 'cx/gpt-6-luna', system: '', user: '' };
                                        break;
                                      case 'wait':
                                        newContent = { waitTime: 30 };
                                        break;
                                      case 'voice_clone':
                                        newContent = { voiceCloneId: '', text: '' };
                                        break;
                                      default:
                                        newContent = { url: '', caption: '' };
                                        break;
                                    }

                                    const currentSequence = activeSequence;
                                    const newSequence = currentSequence.map((seqItem, i) =>
                                      i === index ? { type: newType, content: newContent } : seqItem
                                    );
                                    setActiveSequence(newSequence);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="text">💬 Texto</option>
                                  <option value="image">🖼️ Imagem</option>
                                  <option value="video">🎬 Vídeo</option>
                                  <option value="audio">🎵 Áudio</option>
                                  <option value="voice_clone">🔊 Voz clonada</option>
                                  <option value="document">📄 Arquivo</option>
                                  <option value="openai">🤖 OpenAI</option>
                                  {/* Groq oculto: <option value="groq">⚡ Groq AI</option> */}
                                  <option value="wait">⏱️ Espera</option>
                                </select>

                                {item.type === 'text' && (
                                  <div className="space-y-2">
                                    {/* Checkbox para usar variações */}
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        id={`useVariations-${index}`}
                                        checked={item.content.useVariations || false}
                                        onChange={(e) => {
                                          const currentSequence = activeSequence;
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: {
                                                ...seqItem.content,
                                                useVariations: e.target.checked,
                                                variations: e.target.checked ? [''] : undefined,
                                                text: e.target.checked ? undefined : seqItem.content.text
                                              }
                                            } : seqItem
                                          );
                                          setActiveSequence(newSequence);
                                        }}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                      />
                                      <label htmlFor={`useVariations-${index}`} className="text-sm font-medium text-gray-700">
                                        Usar variações de texto
                                      </label>
                                    </div>

                                    {item.content.useVariations ? (
                                      /* Modo variações */
                                      <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                          Variações de texto
                                        </label>
                                        {(item.content.variations || ['']).map((variation: string, varIndex: number) => (
                                          <div key={varIndex} className="flex gap-2">
                                            <div className="flex-1">
                                              <textarea
                                                placeholder={`Variação ${varIndex + 1}... Use variáveis como {{nome}}, {{primeironome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}`}
                                                value={variation}
                                                onChange={(e) => {
                                                  const currentSequence = activeSequence;
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations[varIndex] = e.target.value;
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setActiveSequence(newSequence);
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                rows={2}
                                              />
                                            </div>
                                            {(item.content.variations || []).length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const currentSequence = activeSequence;
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations.splice(varIndex, 1);
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setActiveSequence(newSequence);
                                                }}
                                                className="px-2 py-1 text-red-600 hover:text-red-800"
                                                title="Remover variação"
                                              >
                                                ✕
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = activeSequence;
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                const newVariations = [...(seqItem.content.variations || []), ''];
                                                return {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, variations: newVariations }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setActiveSequence(newSequence);
                                          }}
                                          className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded hover:bg-blue-200"
                                        >
                                          <span>+</span>
                                          Nova variação
                                        </button>
                                      </div>
                                    ) : (
                                      /* Modo texto único */
                                      <div>
                                        <textarea
                                          placeholder="Digite sua mensagem... Use variáveis como {{nome}}, {{primeironome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                          value={item.content.text || ''}
                                          onChange={(e) => {
                                            const currentSequence = activeSequence;
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? { ...seqItem, content: { text: e.target.value } } : seqItem
                                            );
                                            setActiveSequence(newSequence);
                                          }}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          rows={3}
                                        />
                                      </div>
                                    )}

                                    <div className="flex flex-wrap gap-1">
                                      <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                      {['{{nome}}', '{{primeironome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                        <button
                                          key={variable}
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = activeSequence;
                                            if (item.content.useVariations) {
                                              // Adicionar à primeira variação vazia ou criar nova
                                              const variations = item.content.variations || [''];
                                              let targetIndex = variations.findIndex(v => !v.trim());
                                              if (targetIndex === -1) targetIndex = 0;

                                              const newSequence = currentSequence.map((seqItem, i) => {
                                                if (i === index) {
                                                  const newVariations = [...variations];
                                                  newVariations[targetIndex] = (newVariations[targetIndex] || '') + variable;
                                                  return {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, variations: newVariations }
                                                  };
                                                }
                                                return seqItem;
                                              });
                                              setActiveSequence(newSequence);
                                            } else {
                                              // Adicionar ao texto único
                                              const currentText = currentSequence[index]?.content?.text || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? { ...seqItem, content: { text: currentText + variable } } : seqItem
                                              );
                                              setActiveSequence(newSequence);
                                            }
                                          }}
                                          className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                        >
                                          {variable}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {item.type === 'voice_clone' && (
                                  <VoiceCloneCampaignItem
                                    voices={voiceClones}
                                    voiceCloneId={item.content.voiceCloneId || ''}
                                    text={item.content.text || ''}
                                    onChange={(patch) => {
                                      const currentSequence = activeSequence;
                                      const newSequence = currentSequence.map((seqItem, i) =>
                                        i === index ? { ...seqItem, content: { ...seqItem.content, ...patch } } : seqItem
                                      );
                                      setActiveSequence(newSequence);
                                    }}
                                  />
                                )}

                                {item.type === 'openai' && (
                                  <div className="space-y-4">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-blue-600 font-medium">🤖 OpenAI</span>
                                      </div>
                                      <p className="text-sm text-blue-600">
                                        Configure os parâmetros para gerar mensagens personalizadas usando inteligência artificial
                                      </p>
                                    </div>

                                    <p className="text-xs text-gray-500">Modelo: <strong>IntegraIA (cx/gpt-6-luna)</strong> — definido pelo sistema.</p>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = activeSequence;
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setActiveSequence(newSequence);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{primeironome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = activeSequence;
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setActiveSequence(newSequence);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{primeironome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = activeSequence;
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setActiveSequence(newSequence);
                                            }}
                                            className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {false && item.type === 'groq' && (
                                  <div className="space-y-4">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-yellow-600 font-medium">⚡ Groq AI</span>
                                      </div>
                                      <p className="text-sm text-yellow-600">
                                        Configure os parâmetros para gerar mensagens personalizadas usando Groq AI com velocidade ultra-rápida
                                      </p>
                                    </div>

                                    <p className="text-xs text-gray-500">Modelo: <strong>IntegraIA (cx/gpt-6-luna)</strong> — definido pelo sistema.</p>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = activeSequence;
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setActiveSequence(newSequence);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{primeironome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = activeSequence;
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setActiveSequence(newSequence);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{primeironome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = activeSequence;
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setActiveSequence(newSequence);
                                            }}
                                            className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded hover:bg-orange-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {['image', 'video', 'audio', 'document'].includes(item.type) && (
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <label className="block text-sm font-medium text-gray-700">
                                        Arquivo
                                      </label>
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={item.content.useMediaVariations || false}
                                          onChange={(e) => {
                                            const currentSequence = activeSequence;
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                return {
                                                  ...seqItem,
                                                  content: {
                                                    ...seqItem.content,
                                                    useMediaVariations: e.target.checked,
                                                    mediaVariations: e.target.checked ? Array.from({ length: 4 }, () => ({ url: '', caption: '', fileName: '' })) : undefined,
                                                    // Limpar URL principal quando ativar variações
                                                    url: e.target.checked ? '' : seqItem.content.url
                                                  }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setActiveSequence(newSequence);
                                          }}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-600">Usar Variações</span>
                                      </label>
                                    </div>

                                    {item.content.useMediaVariations ? (
                                      // Grid horizontal de 4 variações
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {Array.from({ length: 4 }, (_, varIndex) => {
                                          const mediaVariations = item.content.mediaVariations || [];
                                          const variation = mediaVariations[varIndex] || { url: '', caption: '', fileName: '' };
                                          const hasFile = variation.url;

                                          return (
                                            <div key={varIndex} className="space-y-2">
                                              {/* Header da variação */}
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium text-gray-600">
                                                  {varIndex === 0 ? 'Principal' : `Var. ${varIndex + 1}`}
                                                </span>
                                                {hasFile && varIndex > 0 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const currentSequence = activeSequence;
                                                      const newSequence = currentSequence.map((seqItem, i) => {
                                                        if (i === index) {
                                                          const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                          newVariations[varIndex] = { url: '', caption: '', fileName: '' };
                                                          return {
                                                            ...seqItem,
                                                            content: { ...seqItem.content, mediaVariations: newVariations }
                                                          };
                                                        }
                                                        return seqItem;
                                                      });
                                                      setActiveSequence(newSequence);
                                                    }}
                                                    className="text-red-500 hover:text-red-700 text-xs p-0.5"
                                                    title="Remover"
                                                  >
                                                    ✕
                                                  </button>
                                                )}
                                              </div>

                                              {/* Preview quadrado */}
                                              <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                                                {!hasFile ? (
                                                  <>
                                                    <input
                                                      type="file"
                                                      id={`file-upload-${index}-${varIndex}`}
                                                      className="hidden"
                                                      accept={
                                                        item.type === 'image' ? 'image/*' :
                                                        item.type === 'video' ? 'video/*' :
                                                        item.type === 'audio' ? 'audio/*' :
                                                        'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                                      }
                                                      onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                          handleFileUpload(file, index, varIndex);
                                                        }
                                                      }}
                                                      disabled={uploadingFiles[`${activeMessageDraft}:${index}-${varIndex}`]}
                                                    />
                                                    <label
                                                      htmlFor={`file-upload-${index}-${varIndex}`}
                                                      className={`w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                        uploadingFiles[`${activeMessageDraft}:${index}-${varIndex}`]
                                                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                                          : 'text-gray-500 hover:text-blue-600'
                                                      }`}
                                                    >
                                                      {uploadingFiles[`${activeMessageDraft}:${index}-${varIndex}`] ? (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                          <span className="text-xs">Enviando</span>
                                                        </div>
                                                      ) : (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="text-2xl">
                                                            {item.type === 'image' && '🖼️'}
                                                            {item.type === 'video' && '🎥'}
                                                            {item.type === 'audio' && '🎵'}
                                                            {item.type === 'document' && '📄'}
                                                          </div>
                                                          <span className="text-xs text-center">
                                                            Fazer upload
                                                          </span>
                                                        </div>
                                                      )}
                                                    </label>
                                                  </>
                                                ) : (
                                                  <div className="w-full h-full relative group">
                                                    {item.type === 'image' && (
                                                      <img
                                                        src={variation.url}
                                                        alt={`Variação ${varIndex + 1}`}
                                                        className="w-full h-full object-cover"
                                                      />
                                                    )}

                                                    {item.type !== 'image' && (
                                                      <div className="w-full h-full bg-blue-100 rounded flex items-center justify-center text-2xl">
                                                        {getFileIcon(fileInfos[`${activeMessageDraft}:${index}`]?.type || item.type)}
                                                      </div>
                                                    )}
                                                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                                      <div className="bg-white bg-opacity-90 p-1 rounded-full">
                                                        <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                                                          <path d="M8 5v14l11-7z"/>
                                                        </svg>
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>

                                              {/* Campo de legenda para imagem e vídeo */}
                                              {hasFile && ['image', 'video'].includes(item.type) && (
                                                <input
                                                  type="text"
                                                  placeholder="Legenda..."
                                                  value={variation.caption || ''}
                                                  onChange={(e) => {
                                                    const currentSequence = activeSequence;
                                                    const newSequence = currentSequence.map((seqItem, i) => {
                                                      if (i === index) {
                                                        const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                        // Garantir que o array tenha o tamanho necessário
                                                        while (newVariations.length <= varIndex) {
                                                          newVariations.push({ url: '', caption: '', fileName: '' });
                                                        }
                                                        newVariations[varIndex] = { ...newVariations[varIndex], caption: e.target.value };
                                                        return {
                                                          ...seqItem,
                                                          content: { ...seqItem.content, mediaVariations: newVariations }
                                                        };
                                                      }
                                                      return seqItem;
                                                    });
                                                    setActiveSequence(newSequence);
                                                  }}
                                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                />
                                              )}

                                              {/* Nome do arquivo para não-imagens */}
                                              {hasFile && !['image', 'video'].includes(item.type) && (
                                                <div className="text-xs text-gray-600 truncate" title={variation.fileName}>
                                                  {variation.fileName || 'Arquivo'}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      // Interface original de arquivo único
                                      <>
                                        {!item.content.url ? (
                                          <div className="flex items-center gap-3">
                                            <input
                                              type="file"
                                              id={`file-upload-${index}`}
                                              className="hidden"
                                              accept={
                                                item.type === 'image' ? 'image/*' :
                                                item.type === 'video' ? 'video/*' :
                                                item.type === 'audio' ? 'audio/*' :
                                                'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                              }
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                  handleFileUpload(file, index);
                                                }
                                              }}
                                              disabled={uploadingFiles[`${activeMessageDraft}:${index}`]}
                                            />
                                            <label
                                              htmlFor={`file-upload-${index}`}
                                              className={`flex-1 cursor-pointer px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg text-center text-sm font-medium transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                uploadingFiles[`${activeMessageDraft}:${index}`]
                                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                                  : 'bg-white text-gray-700'
                                              }`}
                                            >
                                              {uploadingFiles[`${activeMessageDraft}:${index}`] ? (
                                                <div className="flex flex-col items-center gap-2">
                                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                  <span>Enviando arquivo...</span>
                                                </div>
                                              ) : (
                                                <div className="flex flex-col items-center gap-2">
                                                  <div className="text-2xl">
                                                    {item.type === 'image' && '🖼️'}
                                                    {item.type === 'video' && '🎥'}
                                                    {item.type === 'audio' && '🎵'}
                                                    {item.type === 'document' && '📄'}
                                                  </div>
                                                  <span className="text-xs text-center">
                                                    Fazer upload
                                                  </span>
                                                  <span className="text-xs text-gray-500">
                                                    {item.type === 'image' && 'Imagens: JPG, PNG, GIF, WebP'}
                                                    {item.type === 'video' && 'Vídeos: MP4, AVI, MOV, WMV, MKV'}
                                                    {item.type === 'audio' && 'Áudios: MP3, WAV, OGG, AAC, M4A'}
                                                    {item.type === 'document' && 'Documentos: PDF, DOC, XLS, TXT, ZIP'}
                                                  </span>
                                                </div>
                                              )}
                                            </label>
                                          </div>
                                        ) : (
                                          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-3">
                                                {item.type === 'image' && (
                                                  <img
                                                    src={item.content.url}
                                                    alt="Preview"
                                                    className="w-12 h-12 object-cover rounded"
                                                  />
                                                )}
                                                {item.type !== 'image' && (
                                                  <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center text-2xl">
                                                    {getFileIcon(fileInfos[`${activeMessageDraft}:${index}`]?.type || item.type)}
                                                  </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-sm font-medium text-gray-900 truncate">
                                                    {fileInfos[`${activeMessageDraft}:${index}`]?.name || 'Arquivo carregado'}
                                                  </div>
                                                  <div className="text-xs text-gray-500">
                                                    {fileInfos[`${activeMessageDraft}:${index}`]?.size ? formatFileSize(fileInfos[`${activeMessageDraft}:${index}`].size) : ''}
                                                  </div>
                                                </div>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Remover arquivo"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}

                                    {['image', 'video'].includes(item.type) && !item.content.useMediaVariations && (
                                      <div className="space-y-2">
                                        <input
                                          type="text"
                                          placeholder="Legenda (opcional) - Use variáveis como {{nome}}, {{telefone}}, etc."
                                          value={item.content.caption || ''}
                                          onChange={(e) => {
                                            const currentSequence = activeSequence;
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? {
                                                ...seqItem,
                                                content: { ...seqItem.content, caption: e.target.value }
                                              } : seqItem
                                            );
                                            setActiveSequence(newSequence);
                                          }}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div className="flex flex-wrap gap-1">
                                          <span className="text-xs text-gray-500">Variáveis:</span>
                                          {['{{nome}}', '{{primeironome}}', '{{telefone}}', '{{categoria}}'].map((variable) => (
                                            <button
                                              key={variable}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = activeSequence;
                                                const currentCaption = currentSequence[index]?.content?.caption || '';
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, caption: currentCaption + variable }
                                                  } : seqItem
                                                );
                                                setActiveSequence(newSequence);
                                              }}
                                              className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                            >
                                              {variable}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {item.type === 'document' && !item.content.useMediaVariations && (
                                      <input
                                        type="text"
                                        placeholder="Nome do arquivo (opcional)"
                                        value={item.content.fileName || ''}
                                        onChange={(e) => {
                                          const currentSequence = activeSequence;
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, fileName: e.target.value }
                                            } : seqItem
                                          );
                                          setActiveSequence(newSequence);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                    )}
                                  </div>
                                )}

                                {item.type === 'wait' && (
                                  <div className="space-y-3">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                      <div className="flex items-start gap-3">
                                        <span className="text-2xl">⏱️</span>
                                        <div className="flex-1">
                                          <h4 className="text-sm font-medium text-yellow-900 mb-2">
                                            Tempo de Espera
                                          </h4>
                                          <p className="text-xs text-yellow-700 mb-3">
                                            Aguarda um tempo antes de enviar a próxima mensagem
                                          </p>
                                          <div className="flex items-center gap-2">
                                            <input
                                              type="number"
                                              min="1"
                                              max="3600"
                                              value={item.content.waitTime || 30}
                                              onChange={(e) => {
                                                const currentSequence = activeSequence;
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { waitTime: parseInt(e.target.value) || 30 }
                                                  } : seqItem
                                                );
                                                setActiveSequence(newSequence);
                                              }}
                                              className="w-24 px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                                            />
                                            <span className="text-sm text-yellow-700">segundos</span>
                                          </div>
                                          <p className="text-xs text-yellow-600 mt-2">
                                            Mínimo: 1 segundo | Máximo: 3600 segundos (1 hora)
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {activeSequence.length === 0 && (
                            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                              <div className="text-4xl mb-4">📝</div>
                              <p className="text-lg font-medium">Nenhuma mensagem na sequência</p>
                              <p className="text-sm">Clique em "Adicionar Mensagem" para começar a criar sua campanha.</p>
                            </div>
                          )}
                        </div>
                      </div>
                      </div>
                    ) : (
                      activeMessageDraft === 'default' ? (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">
                          O envio via <strong>API Oficial WhatsApp</strong> aceita apenas <strong>template aprovado</strong>. O texto e as variáveis são definidos no bloco do template oficial.
                        </p>
                      </div>
                      ) : (
                      /* Template próprio do usuário do rodízio. A CONTA continua
                         sendo a da campanha — o que muda por usuário é o template. */
                      <div className="space-y-3">
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                          Escolha o template que <strong>{postSendAssignedUsers.find(u => u.id === activeMessageDraft)?.nome || 'este usuário'}</strong> dispara.
                          Sem escolher nada, ele usa o template padrão da campanha.
                        </div>
                        <select
                          value={perUserTemplates[activeMessageDraft]?.lateTemplateName || ''}
                          onChange={(e) => {
                            const draftKey = activeMessageDraft;
                            const name = e.target.value;
                            if (!name) {
                              setPerUserTemplates(prev => {
                                const next = { ...prev };
                                delete next[draftKey];
                                return next;
                              });
                              return;
                            }
                            const template = lateTemplates.find(item => item.name === name);
                            setPerUserTemplates(prev => ({
                              ...prev,
                              [draftKey]: {
                                lateTemplateName: name,
                                lateTemplateLang: template?.language || '',
                                text: template ? getTemplateBodyText(template) : '',
                                variableOrder: template ? getTemplateOrder(template) : []
                              }
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          <option value="">Usar o template padrão da campanha</option>
                          {lateTemplates
                            .filter(template => isTemplateCompatibleWithCampaign(template))
                            .map(template => (
                              <option key={`${template.name}-${template.language}`} value={template.name}>
                                {template.name} ({template.language})
                              </option>
                            ))}
                        </select>
                        {perUserTemplates[activeMessageDraft]?.text && (
                          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                            <p className="mb-1 text-xs font-semibold text-gray-600">Corpo do template</p>
                            <p className="whitespace-pre-wrap text-sm text-gray-800">{perUserTemplates[activeMessageDraft].text}</p>
                          </div>
                        )}
                      </div>
                      )
                    )}
                  </div>)}
                </div>
                <div className="mt-8 flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-between">
                  <button
                    type="button"
                    onClick={handleCloseCreateModal}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <div className="flex gap-3">
                    {wizardStep > 1 && (
                      <button type="button" onClick={() => setWizardStep(previous => (previous - 1) as WizardStep)} className="flex-1 rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-700 hover:bg-gray-50">
                        Voltar
                      </button>
                    )}
                    {wizardStep < 4 ? (
                      <button type="button" onClick={goToNextWizardStep} className="flex-1 rounded-lg bg-blue-600 px-8 py-3 font-medium text-white shadow hover:bg-blue-700">
                        Continuar
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="flex-1 whitespace-nowrap px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg"
                      >
                        {cloneSourceName ? 'Criar Cópia' : 'Criar Campanha'}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
          </Portal>
        )}

        {/* Modal de Relatórios */}
        {showReportModal && (
          <Portal>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] max-h-[90dvh] overflow-y-auto relative"
                 style={{ zIndex: 1000 }}>
              <div className="flex justify-between items-center p-6 border-b">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-gray-900">Relatório da Campanha</h3>
                  <button
                    onClick={handleRefreshReport}
                    disabled={reportLoading}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Atualizar relatório"
                  >
                    <svg className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {reportLoading ? 'Atualizando...' : 'Atualizar'}
                  </button>
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                {reportLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Carregando relatório...</span>
                  </div>
                ) : reportData ? (
                  <div className="space-y-6">
                    {/* Informações da Campanha */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">{reportData.campaign.nome}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Status:</span>
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reportData.campaign.status)}`}>
                            {getStatusText(reportData.campaign.status)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Criado por:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.createdByName || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Criação:</span>
                          <span className="ml-2 text-gray-900">{new Date(reportData.campaign.criadoEm).toLocaleString('pt-BR')}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Início:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.startedAt ? new Date(reportData.campaign.startedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Conclusão:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.completedAt ? new Date(reportData.campaign.completedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Estatísticas */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Estatísticas de Envio</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-blue-600">{reportData.stats.total}</div>
                          <div className="text-sm text-blue-800">Total de Contatos</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-600">{reportData.stats.sent}</div>
                          <div className="text-sm text-green-800">Mensagens Enviadas</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-red-600">{reportData.stats.failed}</div>
                          <div className="text-sm text-red-800">Mensagens Falharam</div>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-yellow-600">{reportData.stats.pending}</div>
                          <div className="text-sm text-yellow-800">Mensagens Pendentes</div>
                        </div>
                      </div>
                    </div>

                    {/* Mensagens por Sessão */}
                    {Object.keys(reportData.messagesBySession).length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Distribuição por Sessão</h4>
                        <div className="bg-white border rounded-lg overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessão</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enviadas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Falharam</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {Object.entries(reportData.messagesBySession).map(([sessionName, sessionData]) => {
                                const messages = (sessionData as any).messages || [];
                                return (
                                  <tr key={sessionName}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sessionName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{messages.length}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                      {messages.filter((m: any) => m.status === 'SENT').length}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                      {messages.filter((m: any) => m.status === 'FAILED').length}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Mensagens Detalhadas */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">Detalhes das Mensagens</h4>
                        <button
                          onClick={handleDownloadReport}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                        >
                          📊 Download CSV
                        </button>
                      </div>

                      {(() => {
                        const messages = reportData.campaign.messages || [];
                        const totalPages = Math.ceil(messages.length / reportItemsPerPage);
                        const startIndex = (reportCurrentPage - 1) * reportItemsPerPage;
                        const endIndex = startIndex + reportItemsPerPage;
                        const currentMessages = messages.slice(startIndex, endIndex);

                        return (
                          <>
                            <div className="bg-white border rounded-lg overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessão</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data de Envio</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Erro</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {currentMessages.map((message: any) => (
                                    <tr key={message.id}>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.contactName}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.contactPhone}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          message.status === 'SENT' ? 'bg-green-100 text-green-800' :
                                          message.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                          message.status === 'PROCESSING' ? 'bg-blue-100 text-blue-800' :
                                          'bg-yellow-100 text-yellow-800'
                                        }`}>
                                          {message.status === 'SENT' ? 'Enviada' :
                                           message.status === 'FAILED' ? 'Falhou' :
                                           message.status === 'PROCESSING' ? 'Processando' : 'Pendente'}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.sessionName || 'N/A'}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A'}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {message.errorMessage && message.errorMessage !== 'N/A' ? (
                                          (() => {
                                            const errorInfo = getErrorIcon(message.errorMessage);
                                            return errorInfo ? (
                                              <div className="group relative inline-flex items-center">
                                                <div className="flex items-center justify-center w-8 h-8 rounded-full ${errorInfo.bgColor} cursor-help">
                                                  <span className="text-lg">{errorInfo.icon}</span>
                                                </div>

                                                {/* Tooltip com posicionamento inteligente */}
                                                <div className="group-hover:block hidden absolute z-[9999]">
                                                  {/* Versão à direita */}
                                                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700 group-hover/right:block hidden">
                                                    {/* Seta esquerda */}
                                                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-l border-b border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Versão à esquerda (fallback) */}
                                                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700">
                                                    {/* Seta direita */}
                                                    <div className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-r border-t border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <span className="text-sm text-red-600">
                                                {message.errorMessage}
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          <span className="text-sm text-gray-400 flex items-center justify-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                                              <span className="text-lg">✅</span>
                                            </div>
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {messages.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                  Nenhuma mensagem encontrada
                                </div>
                              )}
                            </div>

                            {/* Paginação */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between mt-4">
                                <div className="text-sm text-gray-700">
                                  Mostrando {startIndex + 1} a {Math.min(endIndex, messages.length)} de {messages.length} mensagens
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.max(page - 1, 1))}
                                    disabled={reportCurrentPage === 1}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Anterior
                                  </button>

                                  <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                      <button
                                        key={pageNum}
                                        onClick={() => setReportCurrentPage(pageNum)}
                                        className={`px-3 py-2 text-sm rounded-md ${
                                          pageNum === reportCurrentPage
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                      >
                                        {pageNum}
                                      </button>
                                    ))}
                                  </div>

                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.min(page + 1, totalPages))}
                                    disabled={reportCurrentPage === totalPages}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Próximo
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Erro ao carregar relatório
                  </div>
                )}
              </div>
            </div>
          </div>
          </Portal>
        )}

        {/* Modal de Edição de Campanha */}
        {showEditModal && editingCampaign && (
          <Portal>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Editar Campanha</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Ajuste os parâmetros da campanha em andamento
                    </p>
                  </div>
                  <button
                    onClick={() => { setShowEditModal(false); setEditingCampaign(null); }}
                    className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Info banner */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-amber-600 text-lg">⚠️</span>
                      <div>
                        <p className="text-sm font-medium text-amber-800">Campanha em andamento</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Você pode alterar nome, intervalo de randomização{isOfficialCampaign(editingCampaign) ? '' : ', sessões WhatsApp'} e horários de disparo sem interromper a campanha.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status atual */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(editingCampaign.status)}`}>
                      {getStatusText(editingCampaign.status)}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({editingCampaign.sentCount}/{editingCampaign.totalContacts} enviados)
                    </span>
                  </div>

                  {/* Nome da Campanha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Campanha
                    </label>
                    <input
                      type="text"
                      value={editFormData.nome}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, nome: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Intervalo de Randomização */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Intervalo de Randomização (segundos)
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Mínimo</label>
                        <input
                          type="number"
                          min="0"
                          max="600"
                          value={editFormData.randomDelay}
                          onChange={(e) => {
                            const nextMin = Math.min(parseInt(e.target.value) || 0, 600)
                            setEditFormData(prev => ({
                              ...prev,
                              randomDelay: nextMin,
                              randomDelayMax: Math.max(prev.randomDelayMax ?? prev.randomDelay, nextMin)
                            }))
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <span className="text-gray-400 font-medium mt-5">a</span>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Máximo</label>
                        <input
                          type="number"
                          min={editFormData.randomDelay}
                          max="600"
                          value={editFormData.randomDelayMax ?? editFormData.randomDelay}
                          onChange={(e) => {
                            const parsedValue = parseInt(e.target.value) || 0
                            const nextMax = Math.min(Math.max(parsedValue, editFormData.randomDelay), 600)
                            setEditFormData(prev => ({ ...prev, randomDelayMax: nextMax }))
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      ⏱️ Intervalo aleatório entre envios ({editFormData.randomDelay}s - {editFormData.randomDelayMax ?? editFormData.randomDelay}s)
                    </p>
                  </div>

                  {/* Conexões WhatsApp */}
                  {isOfficialCampaign(editingCampaign) ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conta WhatsApp Oficial
                      </label>
                      <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-green-800">
                          {lateAccounts.find(acc => (acc.platformAccountId || acc.id) === editingCampaign.lateAccountId)?.accountName || 'Conta oficial vinculada'}
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Template: {editingCampaign.lateTemplateName || 'N/A'}{editingCampaign.lateTemplateLang ? ` (${editingCampaign.lateTemplateLang})` : ''}
                        </p>
                        <p className="text-xs text-green-700 mt-2">
                          Campanhas via API Oficial usam a conta do template aprovado e não dependem de conexões Nãp Oficiais.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Conexões WhatsApp ({editFormData.sessionNames.length} selecionadas)
                        </label>
                        {whatsappSessions.length > 0 && (
                          <button
                            type="button"
                            onClick={handleEditSelectAllSessions}
                            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium underline"
                          >
                            {editFormData.sessionNames.length === whatsappSessions.length ? 'Desmarcar todas' : 'Selecionar todas'}
                          </button>
                        )}
                      </div>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {whatsappSessions.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma conexão WhatsApp disponível</p>
                        ) : (
                          whatsappSessions.map((session) => (
                            <label key={session.name} className="flex items-center justify-between space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={editFormData.sessionNames.includes(session.name)}
                                  onChange={() => handleEditSessionToggle(session.name)}
                                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div>
                                  <span className="text-sm font-medium text-gray-700">
                                    {session.mePushName || session.displayName || session.name}
                                  </span>
                                  <span className="text-xs text-gray-500 block">({session.displayName || session.name})</span>
                                </div>
                              </div>
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                Ativa
                              </span>
                              <WahaHealthAlert health={session.health} compact />
                            </label>
                          ))
                        )}
                      </div>
                      {editFormData.sessionNames.length > 0 && (
                        <>
                          <p className="text-xs text-green-600 mt-2">✅ {editFormData.sessionNames.length} conexão(ões) selecionada(s)</p>
                          {whatsappSessions
                            .filter((session) => editFormData.sessionNames.includes(session.name) && session.health?.hasIssue)
                            .map((session) => (
                              <WahaHealthAlert key={session.name} health={session.health} className="mt-2" />
                            ))}
                        </>
                      )}
                    </div>
                  )}

                  {/* Horários e Dias de Disparo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 Horários e Dias de Disparo
                    </label>
                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {/* Dias da Semana */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Dias da Semana
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Dia de Início</label>
                            <select
                              value={editFormData.scheduleStartDay ?? ''}
                              onChange={(e) => setEditFormData(prev => ({
                                ...prev,
                                scheduleStartDay: e.target.value ? parseInt(e.target.value) : null
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="">Sem restrição</option>
                              <option value="0">Domingo</option>
                              <option value="1">Segunda-feira</option>
                              <option value="2">Terça-feira</option>
                              <option value="3">Quarta-feira</option>
                              <option value="4">Quinta-feira</option>
                              <option value="5">Sexta-feira</option>
                              <option value="6">Sábado</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Dia de Fim</label>
                            <select
                              value={editFormData.scheduleEndDay ?? ''}
                              onChange={(e) => setEditFormData(prev => ({
                                ...prev,
                                scheduleEndDay: e.target.value ? parseInt(e.target.value) : null
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="">Sem restrição</option>
                              <option value="0">Domingo</option>
                              <option value="1">Segunda-feira</option>
                              <option value="2">Terça-feira</option>
                              <option value="3">Quarta-feira</option>
                              <option value="4">Quinta-feira</option>
                              <option value="5">Sexta-feira</option>
                              <option value="6">Sábado</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Horários */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Horários
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Hora de Início</label>
                            <select
                              value={editFormData.scheduleStartHour ?? ''}
                              onChange={(e) => setEditFormData(prev => ({
                                ...prev,
                                scheduleStartHour: e.target.value ? parseInt(e.target.value) : null
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="">Sem restrição</option>
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Hora de Fim</label>
                            <select
                              value={editFormData.scheduleEndHour ?? ''}
                              onChange={(e) => setEditFormData(prev => ({
                                ...prev,
                                scheduleEndHour: e.target.value ? parseInt(e.target.value) : null
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="">Sem restrição</option>
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botões */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => { setShowEditModal(false); setEditingCampaign(null); }}
                      className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEditCampaign}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-medium transition-all shadow-lg"
                    >
                      Salvar Alterações
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Portal>
        )}
      </div>
    </>
  );
}
